/**
 * 3D City Explorer — Coast Terminal
 * ---------------------------------------------------------------------
 * A cinematic, free-roaming 3D version of Los Angeles, anchored on
 * The Parker. Browser-only, free tech (Three.js + React Three Fiber +
 * drei + OSM via Overpass). The goal: spatial intuition + emotional
 * attachment to buildings, not GIS.
 *
 * What this slice ships:
 *   • Real OSM footprints extruded with height heuristics — within
 *     800m of The Parker by default for a real-block scale view.
 *   • Atmospheric sky (drei <Sky>), distance fog, layered ambient +
 *     warm directional sun.
 *   • Procedural distant hill silhouettes so the horizon doesn't read
 *     as a black void.
 *   • Building materials with hue variation, height-driven roughness,
 *     and a soft emissive "window-line" top edge.
 *   • A free cinematic camera: drag to orbit, scroll to zoom, WASD to
 *     glide the orbit target across the city, hold Shift to glide
 *     faster, hit F to refit the scene.
 *   • Time-of-day slider drives sun position + sky tint + ambient warmth
 *     (Dawn → Day → Dusk → Night).
 *   • Click any building → cinematic flyTo + glassy card with stats,
 *     rarity tier, and a Capture button. Portfolio overlay groups your
 *     captures by tier in trading-card style.
 *
 * Everything is intentionally still in a single file — easier to iterate
 * on visual polish without ping-ponging across modules. Once the
 * aesthetic is locked we can split.
 */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useRef, useState, Suspense } from 'react';
import axios from 'axios';
import { Canvas, useFrame, useThree, ThreeEvent } from '@react-three/fiber';
import { MapControls, Sky, Line, Billboard, Instances, Instance, Cloud, Clouds, Text } from '@react-three/drei';
import { EffectComposer, SSAO, Bloom, Vignette, BrightnessContrast, HueSaturation, SMAA } from '@react-three/postprocessing';
import { BlendFunction } from 'postprocessing';
import * as THREE from 'three';
import PhotorealCity from './PhotorealCity';

const GOOGLE_KEY_STORAGE = 'coast.google_tiles_key';

const API = 'https://coast-terminal-production.up.railway.app';
const ACCENT = '#C9A96E';

// Default anchor — used only as a fallback before /explore/anchor
// resolves the real, sheet-driven coordinates. The runtime PARKER_LAT
// / PARKER_LNG come from the backend so the Explorer can never drift
// off the address the user has actually tracked. They're `let` so a
// successful /explore/anchor fetch can update them in place; downstream
// fetches are gated on `anchorReady` so geometry is always built using
// the resolved values.
// On the 5900 block of W Pico Blvd. Verified against OSM.
const PARKER_LAT_DEFAULT = 34.04930;
const PARKER_LNG_DEFAULT = -118.36750;
let PARKER_LAT = PARKER_LAT_DEFAULT;
let PARKER_LNG = PARKER_LNG_DEFAULT;

interface BuildingRow {
  osm_id:       string;
  name:         string;
  address:      string;
  ring:         Array<[number, number]>;   // [lng, lat] pairs
  lat:          number;
  lng:          number;
  height_m:     number;
  footprint_m2: number;
  tier:         string;
  tier_color:   string;
  tier_score:   number;
  use:          string;
  levels?:      string;
}
interface BuildingsResponse {
  lat: number; lng: number; radius_m: number;
  count: number; buildings: BuildingRow[];
  elapsed_s: number; fetched_at: string;
}
interface RoadRow {
  osm_id: string;
  kind:   string;
  width:  number;
  tier:   number;
  color:  string;
  name:   string;
  path:   Array<[number, number]>;
}
interface RoadsResponse {
  lat: number; lng: number; radius_m: number;
  count: number; roads: RoadRow[];
}
interface LandUseRow {
  osm_id:   string;
  kind:     string;
  color:    string;
  priority: number;
  name:     string;
  rings:    Array<Array<[number, number]>>;
}
interface LandUseResponse {
  lat: number; lng: number; radius_m: number;
  count: number; parcels: LandUseRow[];
}

/** Intel POI — reused from the Intel side panel so the 3D world shows
 * the same restaurants / gyms / cafes the user already saw mapped. */
interface IntelPOI {
  id:         string;
  lat:        number;
  lng:        number;
  name:       string;
  category:   string;
  ring:       'life' | 'social' | 'work';
  distance_m: number;
}
interface IntelBundle {
  property: {
    lat: number; lng: number;
    name: string; address?: string;
  };
  categories: Array<{ key: string; label: string; color: string; target: number }>;
  pois: IntelPOI[];
}

type ExploreMode = 'orbit' | 'walk';

/** Warmer LA building palette. Real Mid-City / Carthay stock is mostly
 * stucco beige + cream + concrete grey + warm brick. Each building gets
 * one of these picked deterministically from its OSM id so the city
 * reads as varied + warm, not navy-blue void. */
const BUILDING_PALETTE = [
  '#c4a780',  // warm stucco
  '#d6c3a3',  // cream
  '#a89a82',  // weathered tan
  '#9a8b73',  // adobe
  '#8c8276',  // concrete grey
  '#7d7066',  // dark concrete
  '#b89a72',  // gold-stucco
  '#a06854',  // brick
  '#8a6a54',  // dark brick
  '#c2b594',  // sandstone
];
function paletteFor(osmId: string): string {
  let h = 0;
  for (let i = 0; i < osmId.length; i++) h = (h * 31 + osmId.charCodeAt(i)) | 0;
  return BUILDING_PALETTE[Math.abs(h) % BUILDING_PALETTE.length];
}
interface Props {
  token: string;
  onExitToMap: () => void;
  onExitToDashboard: () => void;
}

/* ── Canvas Error Boundary ────────────────────────────────────────────
 *  Catches any render-time error from the Three.js / R3F tree below it
 *  so a single broken plugin, missing asset, or runtime exception
 *  inside the Canvas can't blank the entire Explorer page. Without
 *  this, the next subtle Canvas crash repeats the historical "fully
 *  blank white page" bug that the bulletproof-anchor change hit.
 */
interface CanvasErrorBoundaryState { error: Error | null; }
class CanvasErrorBoundary extends React.Component<
  { fallback: React.ReactNode; children: React.ReactNode },
  CanvasErrorBoundaryState
> {
  state: CanvasErrorBoundaryState = { error: null };
  static getDerivedStateFromError(error: Error): CanvasErrorBoundaryState {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Explorer Canvas crashed:', error, info);
  }
  render() {
    if (this.state.error) return this.props.fallback;
    return this.props.children;
  }
}

/* ── Coord projection ───────────────────────────────────────────────── */
const M_PER_DEG_LAT = 111_320;
function project(lat: number, lng: number): [number, number] {
  const mPerDegLng = 111_320 * Math.cos((PARKER_LAT * Math.PI) / 180);
  const x = (lng - PARKER_LNG) * mPerDegLng;
  const z = -(lat - PARKER_LAT) * M_PER_DEG_LAT;
  return [x, z];
}

/* ── Time of day ─────────────────────────────────────────────────────
 *  0..24-hour mapped to sun angle + tinting. We expose only four
 *  named presets in the UI but the slider produces a continuous value.
 */
type TimeOfDay = number;  // 0..24

interface SunState {
  azimuth:     number;   // drei Sky azimuth (0..360)
  inclination: number;   // drei Sky inclination (0..0.5; lower = higher sun)
  sunDir:      THREE.Vector3;
  sunIntensity: number;
  ambientColor: string;
  ambientIntensity: number;
  fogColor:     string;
  fogNear:      number;
  fogFar:       number;
  bgColor:      string;
  groundColor:  string;
  windowGlow:   number;  // 0..1 — how lit the buildings' top edges look
  rayleigh:     number;
}

/* DAY-PINNED. The previous sunset bias made the city feel cold and dark.
 * The user wanted a real daytime LA — green grass, blue sky, dark
 * streets, light sidewalks. We pin the visual to bright midday and
 * ignore the hour argument entirely (kept as a parameter so any old
 * call sites still type-check). */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function sunStateForHour(_hour: TimeOfDay): SunState {
  // High-noon LA. Sun a bit south-west of zenith, sky bright blue,
  // ambient strong so shadows aren't crushed. Window glow is OFF so
  // we don't get lit-window patches blowing out across the day scene.
  const inclination = 0.18;          // ~32° from zenith
  const azimuth = 0.28;              // sun south-west
  const sunPhi = inclination * Math.PI;
  const sunTheta = azimuth * 2 * Math.PI;
  const sunDir = new THREE.Vector3(
    Math.sin(sunPhi) * Math.cos(sunTheta),
    Math.cos(sunPhi),
    Math.sin(sunPhi) * Math.sin(sunTheta),
  ).normalize();

  return {
    azimuth,
    inclination,
    sunDir,
    sunIntensity:     1.4,
    ambientColor:     '#cfe2ff',
    ambientIntensity: 0.95,
    fogColor:         '#bfd4ee',
    fogNear:          1200,
    fogFar:           7500,
    bgColor:          '#a8c5e3',
    // Warm tan bare-earth tone. Lets the asphalt streets + concrete
    // sidewalks visually pop against it — the previous dark grey
    // ground was nearly identical to asphalt and ate the road grid.
    groundColor:      '#6e5d44',
    windowGlow:       0.0,
    rayleigh:         1.4,
  };
}


/* ── Windowed building shader injection ─────────────────────────────
 *  Inserts a window pattern + cornice line into MeshStandardMaterial's
 *  fragment shader via onBeforeCompile, keeping all the PBR lighting,
 *  shadows, and ACES tone mapping the city already uses. Each side
 *  face (vertical wall) is striped with floor-by-floor window rows;
 *  windows darken the diffuse during the day so the wall has visible
 *  structure, and emissively light up at night based on a deterministic
 *  per-window random mask. Skips roofs, ground line, and The Parker.
 *
 *  Uniforms updated per-render via a captured shader reference so
 *  windowGlow tracks the time-of-day slider live.
 */
const BUILDING_VERTEX_HEAD = `
  varying vec3 vWinPos;
  varying vec3 vWinNorm;
`;
const BUILDING_VERTEX_BODY = `
  vWinPos = transformed;
  vWinNorm = normalize(mat3(modelMatrix) * normal);
`;
const BUILDING_FRAG_HEAD = `
  varying vec3 vWinPos;
  varying vec3 vWinNorm;
  uniform float uWindowGlow;
  uniform float uBuildingSeed;
  uniform float uBuildingHeight;
  uniform float uIsParker;

  float coastHash(float p) {
    return fract(sin(p * 12.9898) * 43758.5453);
  }
`;
const BUILDING_FRAG_BODY = `
  // Only stripe vertical-ish faces (walls). Skip rooftops + ground edge.
  float wallness = 1.0 - abs(vWinNorm.y);
  if (wallness > 0.55 && uBuildingHeight > 4.0) {
    float floorH = 3.3;
    float winInsetX = 0.55;
    float winInsetY = 0.75;
    float winH = 2.05;
    float colPitch = 2.7;

    float yLocal = vWinPos.y;
    float yIn = mod(yLocal, floorH);
    float floorN = floor(yLocal / floorH);

    // Horizontal coord along the face — pick the axis the normal is
    // NOT aligned with.
    float horiz = (abs(vWinNorm.x) > abs(vWinNorm.z)) ? vWinPos.z : vWinPos.x;
    float colN = floor((horiz + 1000.0) / colPitch);
    float hIn = mod(horiz + 1000.0, colPitch);

    // Skip top parapet (uBuildingHeight - 0.8 down) and ground-line edge.
    float topCap   = step(yLocal, uBuildingHeight - 0.9);
    float botCap   = step(0.7, yLocal);

    // Window rectangle inside each (floor × column) cell
    float wV = step(winInsetY, yIn) * step(yIn, winInsetY + winH);
    float wH = step(winInsetX, hIn) * step(hIn, colPitch - winInsetX);
    float winMask = wV * wH * topCap * botCap;

    // Per-window random lit state
    float winId = floorN * 31.0 + colN * 7.0 + uBuildingSeed * 13.0;
    float lit = step(0.42, coastHash(winId));

    // Darken diffuse so windows are visible by day
    vec3 winDark = vec3(0.04, 0.055, 0.085);
    diffuseColor.rgb = mix(diffuseColor.rgb, winDark, winMask * 0.78);

    // Emissive glow for lit windows (driven by time-of-day). Parker
    // gets no special tint or always-on glow at the building level —
    // its identity is marked by a separate gold pin overhead.
    vec3 winLit = vec3(1.0, 0.82, 0.5);
    totalEmissiveRadiance += winLit * winMask * lit * uWindowGlow * 1.5;

    // Subtle horizontal floor line at each floor's base
    float floorLine = (1.0 - step(0.07, yIn)) * topCap * botCap;
    diffuseColor.rgb *= mix(1.0, 0.78, floorLine * 0.5);

    // Cornice / parapet darker band near the top
    float cornice = step(uBuildingHeight - 1.0, yLocal) * step(yLocal, uBuildingHeight - 0.05);
    diffuseColor.rgb *= mix(1.0, 0.78, cornice);
  }
`;


/* ── Single building extruded from its OSM ring ─────────────────────── */
function Building({
  b, isParker, isHovered, isSelected, isCaptured, windowGlow,
  onClick, onHover,
}: {
  b: BuildingRow;
  isParker: boolean;
  isHovered: boolean;
  isSelected: boolean;
  isCaptured: boolean;
  windowGlow: number;
  onClick: (b: BuildingRow) => void;
  onHover: (id: string | null) => void;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const topRef  = useRef<THREE.Mesh>(null);
  // Captured at shader-compile time so we can update windowGlow without
  // recompiling. Each material instance has its own shader reference.
  const shaderRef = useRef<any>(null);
  const buildingSeed = useMemo(() => {
    let h = 0;
    for (let i = 0; i < b.osm_id.length; i++) h = (h * 31 + b.osm_id.charCodeAt(i)) | 0;
    return Math.abs(h) % 9973;
  }, [b.osm_id]);

  // Keep the live windowGlow uniform in sync with the time-of-day slider.
  useEffect(() => {
    if (shaderRef.current) {
      shaderRef.current.uniforms.uWindowGlow.value = windowGlow;
    }
  }, [windowGlow]);

  const onBeforeCompile = useMemo(() => (shader: any) => {
    shader.uniforms.uWindowGlow    = { value: windowGlow };
    shader.uniforms.uBuildingSeed  = { value: buildingSeed };
    shader.uniforms.uBuildingHeight = { value: Math.max(2.5, b.height_m) };
    shader.uniforms.uIsParker      = { value: isParker ? 1.0 : 0.0 };
    shaderRef.current = shader;

    // Vertex: surface local position + world-space normal
    shader.vertexShader = (BUILDING_VERTEX_HEAD + shader.vertexShader)
      .replace('#include <begin_vertex>',
               '#include <begin_vertex>\n' + BUILDING_VERTEX_BODY);

    // Fragment: inject helpers + window/cornice logic before lighting
    shader.fragmentShader = (BUILDING_FRAG_HEAD + shader.fragmentShader)
      .replace('#include <emissivemap_fragment>',
               '#include <emissivemap_fragment>\n' + BUILDING_FRAG_BODY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingSeed, b.height_m, isParker]);

  // Deterministic per-building hue variation from osm_id, so the same
  // building always renders the same colour across reloads.
  const variation = useMemo(() => {
    let h = 0;
    for (let i = 0; i < b.osm_id.length; i++) h = (h * 31 + b.osm_id.charCodeAt(i)) | 0;
    const r = ((h >>> 0) % 1000) / 1000;     // 0..1
    return {
      lightness: 0.85 + r * 0.4,             // multiplier on base lightness
      hueShift:  (r - 0.5) * 0.05,           // tiny shift
      jitter:    ((r * 17) % 1) * 0.5,       // for top edge intensity
    };
  }, [b.osm_id]);

  // Build the extruded geometry once per building
  const { geometry, footprintPts, footprintCenter } = useMemo(() => {
    const shape = new THREE.Shape();
    const pts = b.ring.map(([lng, lat]) => project(lat, lng));
    if (pts.length === 0) return { geometry: null, footprintPts: pts, footprintCenter: [0, 0] as [number, number] };
    shape.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) shape.lineTo(pts[i][0], pts[i][1]);
    shape.closePath();
    const geo = new THREE.ExtrudeGeometry(shape, {
      depth: Math.max(2.5, b.height_m),
      bevelEnabled: false,
      curveSegments: 1,
    });
    geo.rotateX(-Math.PI / 2);
    // Footprint centroid for rooftop placement
    let cx = 0, cz = 0;
    for (const [x, z] of pts) { cx += x; cz += z; }
    cx /= pts.length; cz /= pts.length;
    return { geometry: geo, footprintPts: pts, footprintCenter: [cx, cz] as [number, number] };
  }, [b]);

  // Footprint dimensions for rooftop scaling
  const footprintExtent = useMemo(() => {
    if (footprintPts.length === 0) return { w: 0, d: 0 };
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const [x, z] of footprintPts) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
    return { w: maxX - minX, d: maxZ - minZ };
  }, [footprintPts]);

  // (No more Parker pulse — its identity is shown by the overhead pin.)

  if (!geometry) return null;

  // Warm LA-stucco palette per building, modulated by state. The Parker
  // shares the same palette as everything else — its identity is shown
  // by an overhead gold pin (see PropertyMarker below), not by recolouring
  // the whole building.
  const heightT = Math.min(1, b.height_m / 60);
  const paletteColor = paletteFor(b.osm_id);
  const baseRgb: [number, number, number] = isCaptured
    ? hexToRgb(b.tier_color)
    : hexToRgb(paletteColor);
  const v = 0.92 + variation.lightness * 0.16;   // ±8% jitter
  const finalColor = new THREE.Color(
    Math.min(1, baseRgb[0] * v),
    Math.min(1, baseRgb[1] * v),
    Math.min(1, baseRgb[2] * v),
  );

  // Emissive top — windows look lit at dusk/night
  const topEmissive = new THREE.Color(
    Math.min(1, 0.95 + variation.jitter * 0.1),
    Math.min(1, 0.78 + variation.jitter * 0.2),
    Math.min(1, 0.45 + variation.jitter * 0.3),
  );
  const topGlow = isParker
    ? 0.75
    : (windowGlow * (isCaptured ? 0.9 : 0.55)
       + (isHovered || isSelected ? 0.4 : 0));

  // Selection / hover halo. Parker uses the same neutral base as
  // everything else; it doesn't glow on its own anymore.
  const emissiveColor = isSelected
    ? new THREE.Color(b.tier_color)
    : isHovered
      ? new THREE.Color(b.tier_color)
      : topEmissive;
  const emissiveStrength = isSelected ? 0.5 : isHovered ? 0.32 : 0;

  return (
    <group>
      {/* Buildings are intentionally non-interactive — no click, no
          hover. Pure scenery. (Capture flow moved to its own surface.) */}
      <mesh
        ref={meshRef}
        geometry={geometry}
        castShadow
        receiveShadow
        raycast={() => null}>
        <meshStandardMaterial
          color={finalColor}
          emissive={emissiveColor}
          emissiveIntensity={emissiveStrength}
          roughness={isParker ? 0.45 : 0.7 - heightT * 0.25}   // taller = glassier
          metalness={isParker ? 0.3 : 0.18 + heightT * 0.25}
          onBeforeCompile={onBeforeCompile}
        />
      </mesh>
      {/* Rooftop equipment cluster — HVAC boxes, vent stacks, occasional
          solar arrays or cooling towers. Driven by the same per-building
          deterministic seed so layouts are stable across renders. */}
      {(() => {
        const minDim = Math.min(footprintExtent.w, footprintExtent.d);
        const buildingH = Math.max(2.5, b.height_m);
        if (b.footprint_m2 < 240 || minDim < 7 || isParker) return null;

        // Mini-hash so multiple props on the same roof get unique-feeling
        // placement without needing extra seed state.
        const sub = (n: number) => {
          const x = Math.sin((buildingSeed + n) * 12.9898) * 43758.5453;
          return x - Math.floor(x);
        };

        // How many HVAC units (0-3) + whether to add a vent stack + whether
        // to add a solar array, all from the building seed.
        const acCount = b.footprint_m2 > 1500 ? 3 : b.footprint_m2 > 600 ? 2 : 1;
        const hasVent = sub(7) > 0.45;
        const hasSolar = b.footprint_m2 > 600 && sub(11) > 0.55;
        const hasCoolingTower = b.height_m > 28 && sub(13) > 0.6;

        // Helper for placing within the footprint, biased to centre
        const place = (slot: number): [number, number] => {
          const ang = sub(slot * 5) * Math.PI * 2;
          const rad = (0.15 + sub(slot * 9) * 0.30) * Math.min(footprintExtent.w, footprintExtent.d);
          return [
            footprintCenter[0] + Math.cos(ang) * rad,
            footprintCenter[1] + Math.sin(ang) * rad,
          ];
        };

        const elements: React.ReactNode[] = [];

        // HVAC units — wider than tall boxes
        for (let i = 0; i < acCount; i++) {
          const [px, pz] = place(i + 1);
          const w = 1.6 + sub(i * 3 + 1) * 1.6;
          const d = 1.0 + sub(i * 3 + 2) * 1.2;
          const h = 1.0 + sub(i * 3 + 3) * 0.6;
          elements.push(
            <mesh key={`ac-${i}`}
              position={[px, buildingH + h / 2 + 0.1, pz]}
              castShadow receiveShadow>
              <boxGeometry args={[w, h, d]} />
              <meshStandardMaterial
                color={isCaptured ? b.tier_color : '#3a3f48'}
                roughness={0.7} metalness={0.45}
              />
            </mesh>
          );
          // Small fan grille on top
          elements.push(
            <mesh key={`ac-fan-${i}`}
              position={[px, buildingH + h + 0.2, pz]}
              rotation={[-Math.PI / 2, 0, 0]}
              castShadow>
              <cylinderGeometry args={[Math.min(w, d) * 0.35, Math.min(w, d) * 0.35, 0.15, 8]} />
              <meshStandardMaterial color="#1a1d24" roughness={0.6} metalness={0.5} />
            </mesh>
          );
        }

        // Vent stack — thin tall cylinder
        if (hasVent) {
          const [px, pz] = place(8);
          const h = 1.8 + sub(8) * 1.4;
          elements.push(
            <mesh key="vent"
              position={[px, buildingH + h / 2 + 0.1, pz]}
              castShadow>
              <cylinderGeometry args={[0.3, 0.35, h, 8]} />
              <meshStandardMaterial color="#4a4d54" roughness={0.6} metalness={0.55} />
            </mesh>
          );
        }

        // Solar array — flat grid of dark blue panels (one mesh)
        if (hasSolar) {
          const [px, pz] = place(15);
          const panelW = Math.max(2.5, minDim * 0.25);
          const panelD = Math.max(2.0, minDim * 0.20);
          elements.push(
            <mesh key="solar"
              position={[px, buildingH + 0.4, pz]}
              rotation={[-Math.PI / 12, sub(15) * Math.PI, 0]}
              castShadow receiveShadow>
              <boxGeometry args={[panelW, 0.08, panelD]} />
              <meshStandardMaterial
                color="#1e2a4a"
                emissive="#1f3a6a"
                emissiveIntensity={0.06}
                roughness={0.25}
                metalness={0.7}
              />
            </mesh>
          );
        }

        // Cooling tower — taller cylinder for tall buildings only
        if (hasCoolingTower) {
          const [px, pz] = place(22);
          const h = 3.0 + sub(22) * 2.0;
          elements.push(
            <mesh key="ct"
              position={[px, buildingH + h / 2 + 0.1, pz]}
              castShadow>
              <cylinderGeometry args={[0.9, 1.1, h, 10]} />
              <meshStandardMaterial color="#bfbfc6" roughness={0.75} metalness={0.3} />
            </mesh>
          );
        }

        return <>{elements}</>;
      })()}
    </group>
  );
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [
    ((n >> 16) & 255) / 255,
    ((n >>  8) & 255) / 255,
    ( n        & 255) / 255,
  ];
}


/* ── Distant ridge silhouettes ───────────────────────────────────────
 * Subtle backdrop suggesting the Santa Monica + San Gabriel ranges
 * around LA. Kept LOW + FAR so they read as horizon, not as a wall.
 * Painted with the fog color so they blend into the sky.
 */
function MountainRidge({ color }: { color: string }) {
  const geometry = useMemo(() => {
    // Long, low ribbon — wide enough to fill the horizon, short enough
    // to never block the city. 20m base height max.
    const g = new THREE.PlaneGeometry(12000, 220, 120, 1);
    const pos = g.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      if (y > 0) {
        const n1 = Math.sin(x * 0.0021) * 70;
        const n2 = Math.sin(x * 0.0084 + 1.3) * 30;
        const n3 = Math.sin(x * 0.025 + 2.1) * 8;
        pos.setY(i, y + n1 + n2 + n3);
      }
    }
    pos.needsUpdate = true;
    g.computeVertexNormals();
    return g;
  }, []);

  // Two rings, far behind the city so they don't intersect anything.
  return (
    <>
      <mesh geometry={geometry} position={[0, 0, -3800]}>
        <meshStandardMaterial color={color} fog flatShading
          roughness={1} metalness={0} side={THREE.DoubleSide}
          opacity={0.75} transparent />
      </mesh>
      <mesh geometry={geometry} position={[0, -10, 3800]} rotation={[0, Math.PI, 0]}>
        <meshStandardMaterial color={color} fog flatShading
          roughness={1} metalness={0} side={THREE.DoubleSide}
          opacity={0.55} transparent />
      </mesh>
    </>
  );
}


/* ── Road network ────────────────────────────────────────────────────
 * Renders every OSM road inside the radius as a flat, slightly raised
 * strip on the ground plane. Tier sets the colour + opacity + Y offset
 * so motorways sit visually above neighbourhood streets and the city
 * has the kind of layered hierarchy you see in Apple Maps Flyover.
 */
function Roads({
  roads, dayness,
}: {
  roads: RoadRow[];
  dayness: number;   // 0..1; 1 = noon, 0 = night
}) {
  // Build two layered strips per road: a wider concrete sidewalk strip
  // underneath, and the asphalt road strip on top. Batched per tier
  // into a single mesh for perf.
  const built = useMemo(() => {
    if (roads.length === 0) return null;

    const tiers: Record<number, {
      roadPos:   number[]; roadIdx: number[]; roadOff: number;
      walkPos:   number[]; walkIdx: number[]; walkOff: number;
    }> = {
      1: { roadPos: [], roadIdx: [], roadOff: 0, walkPos: [], walkIdx: [], walkOff: 0 },
      2: { roadPos: [], roadIdx: [], roadOff: 0, walkPos: [], walkIdx: [], walkOff: 0 },
      3: { roadPos: [], roadIdx: [], roadOff: 0, walkPos: [], walkIdx: [], walkOff: 0 },
      4: { roadPos: [], roadIdx: [], roadOff: 0, walkPos: [], walkIdx: [], walkOff: 0 },
      5: { roadPos: [], roadIdx: [], roadOff: 0, walkPos: [], walkIdx: [], walkOff: 0 },
    };

    const pushQuad = (
      arr: number[], idx: number[], off: number,
      x1: number, z1: number, x2: number, z2: number,
      halfW: number,
    ): number => {
      const dx = x2 - x1, dz = z2 - z1;
      const len = Math.hypot(dx, dz);
      if (len === 0) return off;
      const nx = -dz / len, nz =  dx / len;
      arr.push(
        x1 + nx * halfW, 0, z1 + nz * halfW,
        x1 - nx * halfW, 0, z1 - nz * halfW,
        x2 + nx * halfW, 0, z2 + nz * halfW,
        x2 - nx * halfW, 0, z2 - nz * halfW,
      );
      idx.push(off, off + 1, off + 2, off + 1, off + 3, off + 2);
      return off + 4;
    };

    // Sidewalk inflate per tier — wider extension on each side of the road
    const sidewalkInflate: Record<number, number> = {
      1: 4.0, 2: 3.5, 3: 3.0, 4: 2.2, 5: 1.4,
    };

    for (const r of roads) {
      const g = tiers[r.tier] || tiers[4];
      const halfW = r.width / 2;
      const halfWalk = halfW + (sidewalkInflate[r.tier] || 2.0);
      const projected: Array<[number, number]> = r.path.map(([lng, lat]) => project(lat, lng));
      for (let i = 0; i < projected.length - 1; i++) {
        const [x1, z1] = projected[i];
        const [x2, z2] = projected[i + 1];
        g.walkOff = pushQuad(g.walkPos, g.walkIdx, g.walkOff, x1, z1, x2, z2, halfWalk);
        g.roadOff = pushQuad(g.roadPos, g.roadIdx, g.roadOff, x1, z1, x2, z2, halfW);
      }
    }

    const out: Array<{
      tier: number;
      roadGeometry: THREE.BufferGeometry | null;
      walkGeometry: THREE.BufferGeometry | null;
    }> = [];
    for (const [tierStr, g] of Object.entries(tiers)) {
      if (g.roadPos.length === 0 && g.walkPos.length === 0) continue;
      const tier = parseInt(tierStr, 10);
      const make = (pos: number[], idx: number[]) => {
        if (pos.length === 0) return null;
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
        geo.setIndex(idx);
        geo.computeVertexNormals();
        return geo;
      };
      out.push({
        tier,
        roadGeometry: make(g.roadPos, g.roadIdx),
        walkGeometry: make(g.walkPos, g.walkIdx),
      });
    }
    return out;
  }, [roads]);

  if (!built) return null;

  // Asphalt is intentionally dark — needs to read as black against the
  // tan ground. Sidewalks are cream concrete, brighter still so the
  // road-sidewalk-ground stack reads as three distinct layers.
  const asphaltDay   = '#2a2a2e';
  const asphaltNight = '#e6c896';
  const sidewalkColor = '#c9c4b3';
  const emissiveStrength = (1 - dayness) * 0.55;

  return (
    <>
      {built.map(({ tier, roadGeometry, walkGeometry }) => (
        <group key={tier}>
          {/* Sidewalk (wider, underneath) */}
          {walkGeometry && (
            <mesh
              geometry={walkGeometry}
              position={[0, 0.05 + tier * 0.005, 0]}
              receiveShadow={false}
              castShadow={false}>
              <meshStandardMaterial
                color={sidewalkColor}
                roughness={0.95}
                metalness={0.0}
              />
            </mesh>
          )}
          {/* Road (narrower, on top of sidewalk) */}
          {roadGeometry && (
            <mesh
              geometry={roadGeometry}
              position={[0, 0.10 + tier * 0.005, 0]}
              receiveShadow={false}
              castShadow={false}>
              <meshStandardMaterial
                color={asphaltDay}
                emissive={asphaltNight}
                emissiveIntensity={emissiveStrength}
                roughness={0.78}
                metalness={0.0}
              />
            </mesh>
          )}
        </group>
      ))}
    </>
  );
}


/* ── Street props: trees, grass, lamps, benches, trash, signs ─────────
 *  Procedurally scattered along road sidewalks. Uses deterministic
 *  hashing of road id + segment so it's stable across reloads (no
 *  flicker between renders). Renders via simple grouped meshes — for
 *  ~500 props across 1000 road segments, plain meshes still hit 60fps
 *  on modern laptops.
 */
function StreetProps({ roads }: { roads: RoadRow[] }) {
  const props = useMemo(() => {
    const trees:    Array<{ x: number; z: number; scale: number; species: number }> = [];
    const lamps:    Array<{ x: number; z: number; rotY: number }> = [];
    const benches:  Array<{ x: number; z: number; rotY: number }> = [];
    const trashes:  Array<{ x: number; z: number }> = [];
    const stops:    Array<{ x: number; z: number; rotY: number }> = [];
    const signs:    Array<{ x: number; z: number; rotY: number }> = [];

    // Deterministic hash so scatter is stable
    const hash = (n: number) => {
      const x = Math.sin(n * 12.9898) * 43758.5453;
      return x - Math.floor(x);
    };

    for (const r of roads) {
      const proj = r.path.map(([lng, lat]) => project(lat, lng));
      const isMain = r.tier <= 3;
      // Step along the road in metres; place a prop on the side at each step
      let cursorBase = parseInt(r.osm_id.replace(/\D/g, '').slice(-6) || '0', 10);

      for (let i = 0; i < proj.length - 1; i++) {
        const [x1, z1] = proj[i];
        const [x2, z2] = proj[i + 1];
        const segLen = Math.hypot(x2 - x1, z2 - z1);
        if (segLen < 1) continue;
        // Trees every ~12m on residential streets, ~18m on mains
        const treePitch = isMain ? 18 : 12;
        const dx = (x2 - x1) / segLen, dz = (z2 - z1) / segLen;
        const nx = -dz, nz = dx;            // perpendicular unit
        const sidewalkOffset = r.width / 2 + 2.4;

        const numTrees = Math.floor(segLen / treePitch);
        for (let t = 0; t < numTrees; t++) {
          const u = (t + 0.5) / numTrees;
          const tx = x1 + (x2 - x1) * u;
          const tz = z1 + (z2 - z1) * u;
          const seed = cursorBase + t * 31;
          const sideSign = hash(seed) > 0.5 ? 1 : -1;
          const px = tx + nx * sidewalkOffset * sideSign + (hash(seed * 7) - 0.5) * 0.8;
          const pz = tz + nz * sidewalkOffset * sideSign + (hash(seed * 11) - 0.5) * 0.8;
          const r1 = hash(seed * 13);
          const r2 = hash(seed * 17);
          // 70% trees, 12% lamps, 8% bench, 5% trash, 3% sign, 2% stop
          if (r1 < 0.70) {
            // LA-weighted species mix: 35% palm, 25% oak, 20% jacaranda,
            // 12% cypress, 8% ficus.
            const sr = hash(seed * 19);
            let species: number;
            if (sr < 0.35) species = 1;
            else if (sr < 0.60) species = 0;
            else if (sr < 0.80) species = 3;
            else if (sr < 0.92) species = 2;
            else species = 4;
            trees.push({
              x: px, z: pz,
              scale: 0.85 + r2 * 0.45,
              species,
            });
          } else if (r1 < 0.82) {
            lamps.push({ x: px, z: pz, rotY: hash(seed * 23) * Math.PI * 2 });
          } else if (r1 < 0.90 && isMain) {
            benches.push({ x: px, z: pz, rotY: Math.atan2(dx, dz) + (sideSign > 0 ? 0 : Math.PI) });
          } else if (r1 < 0.95) {
            trashes.push({ x: px, z: pz });
          } else if (r1 < 0.98) {
            signs.push({ x: px, z: pz, rotY: hash(seed * 29) * Math.PI * 2 });
          } else if (isMain) {
            stops.push({ x: px, z: pz, rotY: Math.atan2(dx, dz) });
          }
        }
        cursorBase += 1000;
      }
    }
    return { trees, lamps, benches, trashes, stops, signs };
  }, [roads]);

  return (
    <>
      {/* Trees — five species mix weighted for an LA-feeling streetscape:
            palms (most common), oaks, jacarandas, cypresses, ficus.
          Each species has its own silhouette + palette so the streets
          feel landscaped, not monoculture. */}
      {props.trees.map((t, i) => {
        const s = t.species;
        if (s === 1) {
          // Palm — tall slender trunk + low fan of fronds on top
          const trunkH = (9 + (i % 4) * 0.8) * t.scale;
          const fanR = 2.4 * t.scale;
          return (
            <group key={`tree-${i}`} position={[t.x, 0, t.z]}>
              <mesh position={[0, trunkH / 2, 0]} castShadow>
                <cylinderGeometry args={[0.18 * t.scale, 0.28 * t.scale, trunkH, 6]} />
                <meshStandardMaterial color="#7a6a4f" roughness={0.9} />
              </mesh>
              {/* Flat fan of fronds: very squashed sphere */}
              <mesh position={[0, trunkH + 0.4, 0]} castShadow scale={[1, 0.35, 1]}>
                <sphereGeometry args={[fanR, 12, 8]} />
                <meshStandardMaterial color="#4a7a3c" roughness={0.85} />
              </mesh>
              {/* Top tuft for a slight crown */}
              <mesh position={[0, trunkH + 0.9, 0]} castShadow>
                <sphereGeometry args={[fanR * 0.45, 8, 6]} />
                <meshStandardMaterial color="#3e6a32" roughness={0.85} />
              </mesh>
            </group>
          );
        }
        if (s === 2) {
          // Cypress / Italian pine — tall narrow cone
          const trunkH = 0.6 * t.scale;
          const coneH = (7 + (i % 3) * 0.6) * t.scale;
          const coneR = 1.1 * t.scale;
          return (
            <group key={`tree-${i}`} position={[t.x, 0, t.z]}>
              <mesh position={[0, trunkH / 2, 0]} castShadow>
                <cylinderGeometry args={[0.16 * t.scale, 0.22 * t.scale, trunkH, 6]} />
                <meshStandardMaterial color="#5a3a1f" roughness={0.95} />
              </mesh>
              <mesh position={[0, trunkH + coneH / 2, 0]} castShadow>
                <coneGeometry args={[coneR, coneH, 10]} />
                <meshStandardMaterial color="#2f5a2e" roughness={0.9} />
              </mesh>
            </group>
          );
        }
        if (s === 3) {
          // Jacaranda — wide flattened canopy, faint purple-green
          const trunkH = 3.4 * t.scale;
          const fW = 2.6 * t.scale;
          const fH = 1.3 * t.scale;
          return (
            <group key={`tree-${i}`} position={[t.x, 0, t.z]}>
              <mesh position={[0, trunkH / 2, 0]} castShadow>
                <cylinderGeometry args={[0.18 * t.scale, 0.28 * t.scale, trunkH, 6]} />
                <meshStandardMaterial color="#6b4a2a" roughness={0.95} />
              </mesh>
              <mesh position={[0, trunkH + fH * 0.55, 0]} castShadow scale={[1, fH / fW, 1]}>
                <sphereGeometry args={[fW, 12, 9]} />
                <meshStandardMaterial color="#5a7d5c" roughness={0.85} />
              </mesh>
            </group>
          );
        }
        if (s === 4) {
          // Ficus / dense low — squat dense canopy
          const trunkH = 1.8 * t.scale;
          const fR = 1.6 * t.scale;
          return (
            <group key={`tree-${i}`} position={[t.x, 0, t.z]}>
              <mesh position={[0, trunkH / 2, 0]} castShadow>
                <cylinderGeometry args={[0.22 * t.scale, 0.32 * t.scale, trunkH, 6]} />
                <meshStandardMaterial color="#5a3a1f" roughness={0.95} />
              </mesh>
              <mesh position={[0, trunkH + fR * 0.55, 0]} castShadow>
                <sphereGeometry args={[fR, 10, 8]} />
                <meshStandardMaterial color="#4d7b3d" roughness={0.88} />
              </mesh>
              {/* Second clump for a fuller silhouette */}
              <mesh position={[fR * 0.5, trunkH + fR * 0.9, fR * 0.2]} castShadow>
                <sphereGeometry args={[fR * 0.7, 8, 6]} />
                <meshStandardMaterial color="#588a4b" roughness={0.88} />
              </mesh>
            </group>
          );
        }
        // Default species 0 — oak (round canopy)
        const trunkH = 3.2 * t.scale;
        const fR = 1.85 * t.scale;
        return (
          <group key={`tree-${i}`} position={[t.x, 0, t.z]}>
            <mesh position={[0, trunkH / 2, 0]} castShadow>
              <cylinderGeometry args={[0.17 * t.scale, 0.25 * t.scale, trunkH, 6]} />
              <meshStandardMaterial color="#5a3a1f" roughness={0.95} />
            </mesh>
            <mesh position={[0, trunkH + fR * 0.55, 0]} castShadow>
              <sphereGeometry args={[fR, 12, 10]} />
              <meshStandardMaterial color="#3c6b32" roughness={0.85} />
            </mesh>
          </group>
        );
      })}
      {/* Lamps */}
      {props.lamps.map((l, i) => (
        <group key={`lamp-${i}`} position={[l.x, 0, l.z]} rotation={[0, l.rotY, 0]}>
          <mesh position={[0, 2.4, 0]} castShadow>
            <cylinderGeometry args={[0.08, 0.12, 4.8, 6]} />
            <meshStandardMaterial color="#3a3d44" roughness={0.6} metalness={0.7} />
          </mesh>
          <mesh position={[0.55, 4.6, 0]} castShadow>
            <boxGeometry args={[1.1, 0.18, 0.32]} />
            <meshStandardMaterial color="#2a2d34" roughness={0.5} metalness={0.7} />
          </mesh>
          <mesh position={[1.0, 4.5, 0]}>
            <sphereGeometry args={[0.22, 8, 6]} />
            <meshBasicMaterial color="#fff0c8" />
          </mesh>
        </group>
      ))}
      {/* Benches */}
      {props.benches.map((b, i) => (
        <group key={`bench-${i}`} position={[b.x, 0, b.z]} rotation={[0, b.rotY, 0]}>
          <mesh position={[0, 0.4, 0]} castShadow>
            <boxGeometry args={[1.6, 0.12, 0.45]} />
            <meshStandardMaterial color="#5e4a30" roughness={0.85} />
          </mesh>
          <mesh position={[0, 0.75, -0.18]} castShadow>
            <boxGeometry args={[1.6, 0.6, 0.08]} />
            <meshStandardMaterial color="#5e4a30" roughness={0.85} />
          </mesh>
          <mesh position={[-0.7, 0.2, 0]}>
            <boxGeometry args={[0.08, 0.4, 0.45]} />
            <meshStandardMaterial color="#3a3d44" metalness={0.4} roughness={0.6} />
          </mesh>
          <mesh position={[0.7, 0.2, 0]}>
            <boxGeometry args={[0.08, 0.4, 0.45]} />
            <meshStandardMaterial color="#3a3d44" metalness={0.4} roughness={0.6} />
          </mesh>
        </group>
      ))}
      {/* Trash cans */}
      {props.trashes.map((t, i) => (
        <group key={`trash-${i}`} position={[t.x, 0, t.z]}>
          <mesh position={[0, 0.55, 0]} castShadow>
            <cylinderGeometry args={[0.35, 0.32, 1.1, 8]} />
            <meshStandardMaterial color="#2f3640" roughness={0.6} metalness={0.5} />
          </mesh>
        </group>
      ))}
      {/* Signs (small upright board) */}
      {props.signs.map((s, i) => (
        <group key={`sign-${i}`} position={[s.x, 0, s.z]} rotation={[0, s.rotY, 0]}>
          <mesh position={[0, 1.4, 0]} castShadow>
            <cylinderGeometry args={[0.04, 0.04, 2.8, 6]} />
            <meshStandardMaterial color="#3a3d44" metalness={0.6} />
          </mesh>
          <mesh position={[0, 2.6, 0]} castShadow>
            <boxGeometry args={[0.7, 0.5, 0.06]} />
            <meshStandardMaterial color="#dcdde1" />
          </mesh>
        </group>
      ))}
      {/* Bus stops — a small shelter with a bench inside */}
      {props.stops.map((s, i) => (
        <group key={`stop-${i}`} position={[s.x, 0, s.z]} rotation={[0, s.rotY, 0]}>
          <mesh position={[0, 1.4, 0]} castShadow>
            <boxGeometry args={[3.6, 2.6, 1.4]} />
            <meshStandardMaterial color="#e4e6ec" transparent opacity={0.35}
              roughness={0.2} metalness={0.5} />
          </mesh>
          <mesh position={[0, 0.4, 0.4]} castShadow>
            <boxGeometry args={[3, 0.18, 0.5]} />
            <meshStandardMaterial color="#5e4a30" roughness={0.85} />
          </mesh>
          <mesh position={[0, 2.85, 0]} castShadow>
            <boxGeometry args={[3.8, 0.12, 1.6]} />
            <meshStandardMaterial color="#3a3d44" metalness={0.5} />
          </mesh>
        </group>
      ))}
    </>
  );
}


/* ── OSM land-use layer ──────────────────────────────────────────────
 *  Parks, parking, water, schools, retail, residential, etc. — pulled
 *  as polygons and rendered as flat coloured patches that sit between
 *  the bare-earth ground and the building layer. Batched per-kind into
 *  a single mesh so the whole layer is ~10 draw calls instead of 10000.
 */
function LandUseLayer({ parcels }: { parcels: LandUseRow[] }) {
  const groups = useMemo(() => {
    const byColor: Record<string, {
      positions: number[];
      indices: number[];
      offset: number;
      priority: number;
    }> = {};
    for (const p of parcels) {
      const slot = byColor[p.color] || (byColor[p.color] = {
        positions: [], indices: [], offset: 0, priority: p.priority,
      });
      slot.priority = Math.max(slot.priority, p.priority);
      for (const ring of p.rings) {
        const pts = ring.map(([lng, lat]) => project(lat, lng));
        if (pts.length < 4) continue;
        let cx = 0, cz = 0;
        for (const [x, z] of pts) { cx += x; cz += z; }
        cx /= pts.length; cz /= pts.length;
        const start = slot.offset;
        slot.positions.push(cx, 0, cz);
        slot.offset += 1;
        for (const [x, z] of pts) {
          slot.positions.push(x, 0, z);
          slot.offset += 1;
        }
        const n = pts.length;
        for (let i = 0; i < n; i++) {
          slot.indices.push(start, start + 1 + i, start + 1 + ((i + 1) % n));
        }
      }
    }
    const out: Array<{
      color: string; geometry: THREE.BufferGeometry; priority: number;
    }> = [];
    for (const [color, slot] of Object.entries(byColor)) {
      if (slot.positions.length === 0) continue;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(slot.positions, 3));
      geo.setIndex(slot.indices);
      geo.computeVertexNormals();
      out.push({ color, geometry: geo, priority: slot.priority });
    }
    out.sort((a, b) => a.priority - b.priority);
    return out;
  }, [parcels]);

  return (
    <>
      {groups.map(({ color, geometry, priority }, i) => (
        <mesh
          key={`landuse-${color}-${i}`}
          geometry={geometry}
          position={[0, 0.02 + priority * 0.002 + i * 0.0008, 0]}
          receiveShadow={false}
          castShadow={false}>
          <meshStandardMaterial color={color} roughness={0.95} metalness={0.0} />
        </mesh>
      ))}
    </>
  );
}


/* ── Parked cars ─────────────────────────────────────────────────────
 *  Deterministic scatter along every road's curb. ~1 car per 8 m on
 *  arterials, sparser on residentials. Adds enormous urban-density
 *  signal compared to empty asphalt.
 */
function ParkedCars({ roads }: { roads: RoadRow[] }) {
  const cars = useMemo(() => {
    const out: Array<{ x: number; z: number; rot: number; color: string }> = [];
    const palette = ['#1c1c1f', '#d9d6cf', '#7a7a82', '#3a4654', '#9b3a2a', '#3f5f43', '#2c4c7a', '#bdb0a0'];
    const hash = (n: number) => {
      const x = Math.sin(n * 12.9898) * 43758.5453;
      return x - Math.floor(x);
    };
    for (const r of roads) {
      if (r.tier > 4) continue;            // skip service alleys
      const proj = r.path.map(([lng, lat]) => project(lat, lng));
      const isMain = r.tier <= 2;
      const carPitch = isMain ? 14 : 8;
      let seed = parseInt(r.osm_id.replace(/\D/g, '').slice(-6) || '0', 10);

      for (let i = 0; i < proj.length - 1; i++) {
        const [x1, z1] = proj[i];
        const [x2, z2] = proj[i + 1];
        const segLen = Math.hypot(x2 - x1, z2 - z1);
        if (segLen < 4) continue;
        const dx = (x2 - x1) / segLen, dz = (z2 - z1) / segLen;
        const nx = -dz, nz = dx;
        const curbOffset = r.width / 2 - 0.9;
        const numCars = Math.floor(segLen / carPitch);
        for (let c = 0; c < numCars; c++) {
          const u = (c + 0.5) / numCars;
          const cx = x1 + (x2 - x1) * u;
          const cz = z1 + (z2 - z1) * u;
          const sideSign = hash(seed) > 0.5 ? 1 : -1;
          // skip 35% of slots so the curb isn't bumper-to-bumper everywhere
          if (hash(seed * 3) > 0.65) { seed += 7; continue; }
          out.push({
            x: cx + nx * curbOffset * sideSign,
            z: cz + nz * curbOffset * sideSign,
            rot: Math.atan2(dx, dz),
            color: palette[Math.floor(hash(seed * 5) * palette.length)],
          });
          seed += 7;
        }
      }
    }
    return out;
  }, [roads]);

  return (
    <>
      {cars.map((c, i) => (
        <group key={`car-${i}`} position={[c.x, 0.6, c.z]} rotation={[0, c.rot, 0]}>
          {/* Body */}
          <mesh castShadow receiveShadow>
            <boxGeometry args={[1.8, 1.2, 4.4]} />
            <meshStandardMaterial color={c.color} roughness={0.45} metalness={0.4} />
          </mesh>
          {/* Cabin / windows (smaller dark box on top) */}
          <mesh position={[0, 0.85, 0]} castShadow>
            <boxGeometry args={[1.65, 0.7, 2.6]} />
            <meshStandardMaterial color="#15171c" roughness={0.2} metalness={0.7} />
          </mesh>
        </group>
      ))}
    </>
  );
}


/* ── Road lane markings ──────────────────────────────────────────────
 *  Yellow centre line on tier 1-2 (arterials), single mesh batched
 *  for the whole network. Subtle but a huge visual unlock.
 */
function LaneMarkings({ roads }: { roads: RoadRow[] }) {
  const geometry = useMemo(() => {
    const positions: number[] = [];
    const indices: number[] = [];
    let offset = 0;
    const dashLen = 3.5;         // metres
    const gapLen  = 3.0;
    const halfStripe = 0.12;     // metres

    for (const r of roads) {
      if (r.tier > 2) continue;  // arterials only
      const proj = r.path.map(([lng, lat]) => project(lat, lng));
      for (let i = 0; i < proj.length - 1; i++) {
        const [x1, z1] = proj[i];
        const [x2, z2] = proj[i + 1];
        const segLen = Math.hypot(x2 - x1, z2 - z1);
        if (segLen < dashLen + gapLen) continue;
        const dx = (x2 - x1) / segLen, dz = (z2 - z1) / segLen;
        const nx = -dz, nz = dx;
        const period = dashLen + gapLen;
        const dashes = Math.floor(segLen / period);
        for (let d = 0; d < dashes; d++) {
          const startT = d * period;
          const endT = startT + dashLen;
          const sx = x1 + dx * startT, sz = z1 + dz * startT;
          const ex = x1 + dx * endT,   ez = z1 + dz * endT;
          // 4 corners offset perpendicular by halfStripe
          positions.push(
            sx + nx * halfStripe, 0, sz + nz * halfStripe,
            sx - nx * halfStripe, 0, sz - nz * halfStripe,
            ex + nx * halfStripe, 0, ez + nz * halfStripe,
            ex - nx * halfStripe, 0, ez - nz * halfStripe,
          );
          indices.push(offset, offset + 1, offset + 2,
                       offset + 1, offset + 3, offset + 2);
          offset += 4;
        }
      }
    }
    if (positions.length === 0) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    g.setIndex(indices);
    g.computeVertexNormals();
    return g;
  }, [roads]);

  if (!geometry) return null;
  return (
    <mesh geometry={geometry} position={[0, 0.13, 0]} receiveShadow={false} castShadow={false}>
      <meshStandardMaterial color="#e8c34a" emissive="#e8c34a" emissiveIntensity={0.15}
        roughness={0.6} metalness={0.0} />
    </mesh>
  );
}


/* ── Moving traffic ──────────────────────────────────────────────────
 *  Picks a curated subset of roads (arterials get priority) and runs
 *  a constant-speed vehicle along each one. Each vehicle wraps at the
 *  end of its path so the city has a continuous low-key flow of cars
 *  without needing a real traffic simulation.
 */
function Traffic({ roads }: { roads: RoadRow[] }) {
  // Build per-vehicle path data once roads change
  const vehicles = useMemo(() => {
    if (roads.length === 0) return [];
    const palette = ['#1c1c1f', '#d9d6cf', '#7a7a82', '#3a4654', '#9b3a2a', '#3f5f43', '#2c4c7a'];
    const out: Array<{
      path:   Array<[number, number]>;   // [x, z] in metres
      cumlen: number[];                  // cumulative arc length up to each vertex
      total:  number;
      speed:  number;                    // metres / second
      color:  string;
      sideSign: 1 | -1;                  // which side of centreline
      curbOffset: number;                // m from centre
    }> = [];
    // Hash for stability
    const hash = (n: number) => {
      const x = Math.sin(n * 12.9898) * 43758.5453;
      return x - Math.floor(x);
    };
    // Sort roads: arterials first, then longest, take ~40
    const sorted = [...roads]
      .filter(r => r.path.length >= 2)
      .sort((a, b) => (a.tier - b.tier) || (b.path.length - a.path.length));
    const pick = sorted.slice(0, 50);
    for (let idx = 0; idx < pick.length; idx++) {
      const r = pick[idx];
      const proj: Array<[number, number]> = r.path.map(([lng, lat]) => project(lat, lng));
      // Cumulative length
      const cum: number[] = [0];
      for (let i = 1; i < proj.length; i++) {
        cum.push(cum[i - 1] + Math.hypot(proj[i][0] - proj[i - 1][0], proj[i][1] - proj[i - 1][1]));
      }
      const total = cum[cum.length - 1];
      if (total < 12) continue;       // skip too-short stubs
      const seed = idx + 1;
      const speed = r.tier <= 2 ? 16 + hash(seed) * 8     // ~35–55 mph arterials
                    : r.tier <= 3 ? 10 + hash(seed) * 6   // ~22–35 mph secondary
                    :                6 + hash(seed) * 4;  // ~13–22 mph residential
      out.push({
        path: proj,
        cumlen: cum,
        total,
        speed,
        color: palette[Math.floor(hash(seed * 3) * palette.length)],
        sideSign: hash(seed * 5) > 0.5 ? 1 : -1,
        // Drive in the centre of one lane: half the road width minus
        // ~1 m so we're not on the curb.
        curbOffset: Math.max(0.5, r.width / 4),
      });
    }
    return out;
  }, [roads]);

  // One Vehicle component per row drives its own motion via useFrame
  return (
    <>
      {vehicles.map((v, i) => (
        <Vehicle key={`car-mv-${i}`} {...v} startOffset={(i * 17) % v.total} />
      ))}
    </>
  );
}

function Vehicle({
  path, cumlen, total, speed, color, sideSign, curbOffset, startOffset,
}: {
  path: Array<[number, number]>;
  cumlen: number[];
  total: number;
  speed: number;
  color: string;
  sideSign: 1 | -1;
  curbOffset: number;
  startOffset: number;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const progress = useRef(startOffset % total);

  useFrame((_, delta) => {
    progress.current += speed * delta;
    if (progress.current >= total) progress.current -= total;

    // Find segment index via cumlen
    let lo = 0, hi = cumlen.length - 1;
    while (lo < hi - 1) {
      const mid = (lo + hi) >> 1;
      if (cumlen[mid] <= progress.current) lo = mid; else hi = mid;
    }
    const i0 = lo, i1 = Math.min(lo + 1, path.length - 1);
    const segLen = cumlen[i1] - cumlen[i0] || 1;
    const t = (progress.current - cumlen[i0]) / segLen;
    const [x1, z1] = path[i0];
    const [x2, z2] = path[i1];
    const dx = x2 - x1, dz = z2 - z1;
    const len = Math.hypot(dx, dz) || 1;
    const nx = -dz / len * curbOffset * sideSign;
    const nz =  dx / len * curbOffset * sideSign;
    const px = x1 + dx * t + nx;
    const pz = z1 + dz * t + nz;
    const heading = Math.atan2(dx, dz) + (sideSign < 0 ? Math.PI : 0);

    if (groupRef.current) {
      groupRef.current.position.set(px, 0.6, pz);
      groupRef.current.rotation.y = heading;
    }
  });

  return (
    <group ref={groupRef}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[1.85, 1.25, 4.5]} />
        <meshStandardMaterial color={color} roughness={0.4} metalness={0.45} />
      </mesh>
      <mesh position={[0, 0.86, 0]} castShadow>
        <boxGeometry args={[1.7, 0.7, 2.7]} />
        <meshStandardMaterial color="#15171c" roughness={0.18} metalness={0.7} />
      </mesh>
      {/* Headlights — small emissive dots at the front */}
      <mesh position={[-0.5, 0.05, 2.0]}>
        <sphereGeometry args={[0.15, 6, 4]} />
        <meshBasicMaterial color="#fff5d8" />
      </mesh>
      <mesh position={[0.5, 0.05, 2.0]}>
        <sphereGeometry args={[0.15, 6, 4]} />
        <meshBasicMaterial color="#fff5d8" />
      </mesh>
    </group>
  );
}


/* ── Per-building sidewalk skirts ──────────────────────────────────
 *  Every building gets a sidewalk-grey "halo" inflated 2m outward from
 *  its footprint. The building's own walls cover the centre, so what
 *  the user sees is a clean ring of pavement wrapping each structure.
 *  Batched into a single BufferGeometry for performance — even at
 *  700+ buildings this is one draw call.
 */
function BuildingSidewalks({ buildings }: { buildings: BuildingRow[] }) {
  const geometry = useMemo(() => {
    const positions: number[] = [];
    const indices: number[] = [];
    let offset = 0;
    // 4 m halo reads cleanly from aerial views. Adjacent buildings'
    // halos overlap, which is what we want — they merge into continuous
    // pavement between buildings.
    const INFLATE = 4.0;

    for (const b of buildings) {
      const pts = b.ring.map(([lng, lat]) => project(lat, lng));
      if (pts.length < 4) continue;
      // Centroid for the triangle fan
      let cx = 0, cz = 0;
      for (const [x, z] of pts) { cx += x; cz += z; }
      cx /= pts.length; cz /= pts.length;
      // Inflate every vertex outward along the centroid → vertex ray
      const inflated: Array<[number, number]> = pts.map(([x, z]) => {
        const dx = x - cx, dz = z - cz;
        const len = Math.hypot(dx, dz);
        return len === 0 ? [x, z] : [x + (dx / len) * INFLATE, z + (dz / len) * INFLATE];
      });
      // Fan-triangulate from centroid. Star-shaped polygons triangulate
      // cleanly this way; truly concave footprints get minor sliver
      // artifacts that the building walls hide anyway.
      const fanStart = offset;
      positions.push(cx, 0, cz);
      offset += 1;
      for (const [x, z] of inflated) {
        positions.push(x, 0, z);
        offset += 1;
      }
      const n = inflated.length;
      for (let i = 0; i < n; i++) {
        indices.push(fanStart, fanStart + 1 + i, fanStart + 1 + ((i + 1) % n));
      }
    }

    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    g.setIndex(indices);
    g.computeVertexNormals();
    return g;
  }, [buildings]);

  return (
    <mesh
      geometry={geometry}
      position={[0, 0.03, 0]}
      receiveShadow={false}
      castShadow={false}>
      <meshStandardMaterial color="#c9c4b3" roughness={0.95} metalness={0.0} />
    </mesh>
  );
}


/* ── Procedural grass patches ───────────────────────────────────────
 *  A handful of green circles scattered around the scene to suggest
 *  lawns / setbacks / small parks. Placed deterministically based on
 *  the road network so they don't clip through the streets.
 */
function GrassPatches({ roads, extent }: { roads: RoadRow[]; extent: number }) {
  const patches = useMemo(() => {
    if (roads.length === 0) return [];
    const out: Array<{ x: number; z: number; r: number; tone: number }> = [];
    const hash = (n: number) => {
      const x = Math.sin(n * 12.9898) * 43758.5453;
      return x - Math.floor(x);
    };
    // 30 patches scattered across the scene radius
    for (let i = 0; i < 35; i++) {
      const angle = hash(i * 7) * Math.PI * 2;
      const dist = hash(i * 11) * extent;
      const x = Math.cos(angle) * dist;
      const z = Math.sin(angle) * dist;
      const r = 6 + hash(i * 13) * 14;
      out.push({ x, z, r, tone: hash(i * 17) });
    }
    return out;
  }, [roads, extent]);

  return (
    <>
      {patches.map((p, i) => {
        const tones = ['#3f6b2e', '#4a7d3a', '#558844'];
        const color = tones[Math.floor(p.tone * 3) % 3];
        return (
          <mesh key={`grass-${i}`}
            rotation={[-Math.PI / 2, 0, 0]}
            position={[p.x, 0.06, p.z]}
            receiveShadow>
            <circleGeometry args={[p.r, 24]} />
            <meshStandardMaterial color={color} roughness={0.95} />
          </mesh>
        );
      })}
    </>
  );
}


/* ── The Parker overhead pin ──────────────────────────────────────────
 *  Small gold marker hovering above the building so users can find it
 *  at a glance without the building itself glowing gold.
 */
function ParkerMarker({ x, z, height }: { x: number; z: number; height: number }) {
  const ref = useRef<THREE.Group>(null);
  useFrame((s) => {
    if (!ref.current) return;
    ref.current.position.y = height + 6 + Math.sin(s.clock.elapsedTime * 1.8) * 0.6;
  });
  return (
    <group ref={ref} position={[x, height + 6, z]}>
      <mesh>
        <sphereGeometry args={[0.9, 16, 12]} />
        <meshStandardMaterial color={ACCENT} emissive={ACCENT} emissiveIntensity={0.85} />
      </mesh>
      <mesh position={[0, -1.2, 0]}>
        <coneGeometry args={[0.55, 1.4, 12]} />
        <meshStandardMaterial color={ACCENT} emissive={ACCENT} emissiveIntensity={0.5} />
      </mesh>
    </group>
  );
}


/* ── Intel POI pins (3D billboards) ──────────────────────────────────
 * Renders every cafe / restaurant / gym / etc. the Intel panel maps
 * as a small floating glow + label above its real lat/lng, colour-
 * coded by category. This is the visual bridge between Intel and the
 * 3D world — the user sees the same neighbourhood, in physical form.
 */
function POIPins({
  pois, categories, showLabels = true,
}: {
  pois: IntelPOI[];
  categories: Array<{ key: string; label: string; color: string }>;
  showLabels?: boolean;
}) {
  const colorMap: Record<string, string> = {};
  for (const c of categories) colorMap[c.key] = c.color;

  return (
    <group>
      {pois.map(p => {
        const [x, z] = project(p.lat, p.lng);
        const color = colorMap[p.category] || '#C9A96E';
        const poleY = 14;
        const labelText = (p.name || '').slice(0, 24);
        return (
          <group key={p.id} position={[x, 0, z]}>
            {/* Soft beam from ground */}
            <mesh position={[0, poleY / 2, 0]}>
              <cylinderGeometry args={[0.3, 0.3, poleY, 8]} />
              <meshBasicMaterial color={color} transparent opacity={0.45} />
            </mesh>
            {/* Glow ball at the top */}
            <mesh position={[0, poleY, 0]}>
              <sphereGeometry args={[1.6, 16, 16]} />
              <meshBasicMaterial color={color} />
            </mesh>
            {/* Outer halo */}
            <mesh position={[0, poleY, 0]}>
              <sphereGeometry args={[3, 16, 16]} />
              <meshBasicMaterial color={color} transparent opacity={0.28} />
            </mesh>
            {/* Name label — billboarded, only when we have a name.
                Text suspends while its font streams from a CDN — isolate
                in its own Suspense so a slow/blocked font fetch can't
                blank the entire scene under the parent Suspense
                (the cause of the "Explorer page goes all white" bug). */}
            {showLabels && labelText && (
              <Billboard position={[0, poleY + 4, 0]}>
                <mesh>
                  <planeGeometry args={[Math.max(6, labelText.length * 0.7), 2.2]} />
                  <meshBasicMaterial color="#0a1322" transparent opacity={0.78}
                    depthWrite={false} />
                </mesh>
                <Suspense fallback={null}>
                  <Text
                    position={[0, 0, 0.05]}
                    fontSize={1.4}
                    color={color}
                    anchorX="center"
                    anchorY="middle"
                    outlineWidth={0.04}
                    outlineColor="#000000">
                    {labelText}
                  </Text>
                </Suspense>
              </Billboard>
            )}
          </group>
        );
      })}
    </group>
  );
}


/* ── First-person Walker ─────────────────────────────────────────────
 * PointerLockControls for COD-style mouse-look + WASD walks the camera
 * across the ground plane at eye height. Holds the camera at a fixed
 * Y, prevents it from going below ground, and clamps to a generous
 * radius so the user doesn't fall off the world.
 */
function Walker({
  active, walkSpeed,
}: {
  active: boolean;
  walkSpeed: { dx: number; dz: number; sprint: boolean };
}) {
  const { camera } = useThree();
  const EYE_HEIGHT = 1.7;
  const initRef = useRef(false);

  useEffect(() => {
    if (!active) return;
    if (initRef.current) return;
    initRef.current = true;
    // Drop the camera to eye level near The Parker, slightly south so
    // the building is in front of you when you enter walk mode.
    camera.position.set(0, EYE_HEIGHT, 28);
    camera.lookAt(0, EYE_HEIGHT, 0);
  }, [active, camera]);

  useEffect(() => {
    if (active) return;
    initRef.current = false;   // reset for next entry
  }, [active]);

  useFrame(() => {
    if (!active) return;
    // Build movement in camera-local space, then project onto the
    // ground plane so vertical look doesn't push us up/down.
    const forward = new THREE.Vector3();
    camera.getWorldDirection(forward);
    forward.y = 0;
    forward.normalize();
    const right = new THREE.Vector3()
      .crossVectors(forward, new THREE.Vector3(0, 1, 0))
      .normalize();
    const speed = walkSpeed.sprint ? 0.55 : 0.22;
    const move = new THREE.Vector3();
    if (walkSpeed.dz !== 0) move.addScaledVector(forward, -walkSpeed.dz * speed);
    if (walkSpeed.dx !== 0) move.addScaledVector(right,   walkSpeed.dx * speed);
    camera.position.add(move);
    camera.position.y = EYE_HEIGHT;
    // Soft clamp: don't wander past 2km from the origin
    const r = Math.hypot(camera.position.x, camera.position.z);
    if (r > 2000) {
      camera.position.x *= 2000 / r;
      camera.position.z *= 2000 / r;
    }
  });

  return null;
}


/* ── Camera orchestrator ─────────────────────────────────────────────
 *  Yields full pan/rotate/zoom control to MapControls; only steps in
 *  for three discrete moves:
 *    1) Initial fit pose after buildings load (one-shot).
 *    2) WASD glide — translates both camera + orbit target together.
 *    3) Cinematic flyTo when a building is selected, lerping until
 *       close enough then handing back to MapControls.
 *  Critically: NO per-frame camera.lookAt() in idle state, so the user
 *  can drag freely without the rig snapping the view back.
 */
function CameraOrchestrator({
  target, fit, glide, arrows,
}: {
  target: { x: number; z: number; height: number } | null;
  fit:    { extent: number; maxHeight: number } | null;
  glide:  { dx: number; dz: number };
  arrows: { yaw: number; pitch: number };
}) {
  const { camera, controls } = useThree() as any;
  const hasFitOnce = useRef(false);
  const flying = useRef(false);
  const desiredPos = useRef(new THREE.Vector3());
  const desiredTgt = useRef(new THREE.Vector3());

  // One-shot fit after buildings load
  useEffect(() => {
    if (!fit || hasFitOnce.current || !controls) return;
    const r = Math.max(120, fit.extent);
    const dist = r * 2.1;
    const height = Math.max(90, Math.min(dist * 0.35, fit.maxHeight * 3 + 80));
    camera.position.set(dist * 0.7, height, dist * 0.7);
    controls.target.set(0, 0, 0);
    controls.update();
    hasFitOnce.current = true;
  }, [fit, camera, controls]);

  // Stage a fly target when selection changes
  useEffect(() => {
    if (!target) {
      flying.current = false;
      return;
    }
    const dist = 80 + target.height * 0.6;
    desiredPos.current.set(
      target.x + dist * 0.85,
      Math.max(60, target.height * 1.5 + 28),
      target.z + dist * 0.85,
    );
    desiredTgt.current.set(target.x, target.height / 2, target.z);
    flying.current = true;
  }, [target]);

  useFrame(() => {
    if (!controls) return;

    // WASD glide: camera-relative. W is forward (the direction the
    // camera is currently facing, projected to the ground plane),
    // S is backward, A/D strafe left/right. Camera + orbit target
    // translate in lock-step so the view doesn't tilt as we move.
    if (glide.dx !== 0 || glide.dz !== 0) {
      const forward = new THREE.Vector3();
      camera.getWorldDirection(forward);
      forward.y = 0;                       // stay horizontal
      if (forward.lengthSq() > 0) forward.normalize();
      const right = new THREE.Vector3()
        .crossVectors(forward, camera.up)
        .normalize();
      // glide.dz is negative when W pressed → multiply by -1 so forward
      // moves us forward; glide.dx is +D / -A → matches right vector.
      const move = new THREE.Vector3()
        .addScaledVector(forward, -glide.dz)
        .addScaledVector(right, glide.dx);
      camera.position.add(move);
      controls.target.add(move);
      controls.update();
    }

    // Arrow-key orbit. Arrow Left/Right yaws around the orbit target;
    // Arrow Up/Down pitches up/down. Pitch is clamped to keep us above
    // the ground and short of straight-down so the user can't flip.
    if (arrows.yaw !== 0 || arrows.pitch !== 0) {
      const YAW_PER_FRAME   = 0.022;   // radians per frame at 60fps ≈ 75°/s
      const PITCH_PER_FRAME = 0.018;
      const offset = new THREE.Vector3().subVectors(camera.position, controls.target);
      const radius = offset.length();
      let theta = Math.atan2(offset.x, offset.z);   // azimuth around +Y
      let phi   = Math.acos(Math.max(-1, Math.min(1, offset.y / radius)));
      theta -= arrows.yaw   * YAW_PER_FRAME;
      phi   += arrows.pitch * PITCH_PER_FRAME;
      // Clamp pitch: keep camera between 5° from vertical and 5° above horizon
      const PHI_MIN = 0.05;
      const PHI_MAX = Math.PI * 0.49;
      phi = Math.max(PHI_MIN, Math.min(PHI_MAX, phi));
      offset.set(
        radius * Math.sin(phi) * Math.sin(theta),
        radius * Math.cos(phi),
        radius * Math.sin(phi) * Math.cos(theta),
      );
      camera.position.copy(controls.target).add(offset);
      controls.update();
    }

    // FlyTo on selection — lerp until close, then stop touching the
    // camera so user can freely orbit/zoom around the selected building.
    if (flying.current) {
      camera.position.lerp(desiredPos.current, 0.07);
      controls.target.lerp(desiredTgt.current, 0.07);
      controls.update();
      if (camera.position.distanceToSquared(desiredPos.current) < 4 &&
          controls.target.distanceToSquared(desiredTgt.current) < 4) {
        flying.current = false;
      }
    }
  });
  return null;
}


/* ── Scene composition ──────────────────────────────────────────────── */
function Scene({
  buildings, roads, parcels, hovered, setHovered, selected, setSelected,
  capturedSet, parkerId, sun, glide, arrows, dayness,
  pois, categories,
}: {
  buildings: BuildingRow[];
  roads: RoadRow[];
  parcels: LandUseRow[];
  hovered: string | null;
  setHovered: (id: string | null) => void;
  selected: BuildingRow | null;
  setSelected: (b: BuildingRow | null) => void;
  capturedSet: Set<string>;
  parkerId: string | null;
  sun: SunState;
  glide: { dx: number; dz: number };
  arrows: { yaw: number; pitch: number };
  dayness: number;
  pois: IntelPOI[];
  categories: Array<{ key: string; label: string; color: string }>;
}) {
  const target = useMemo(() => {
    if (!selected) return null;
    const [x, z] = project(selected.lat, selected.lng);
    return { x, z, height: selected.height_m };
  }, [selected]);

  const fit = useMemo(() => {
    if (buildings.length === 0) return null;
    let maxR = 0, maxH = 0;
    for (const b of buildings) {
      const [x, z] = project(b.lat, b.lng);
      const r = Math.hypot(x, z);
      if (r > maxR) maxR = r;
      if (b.height_m > maxH) maxH = b.height_m;
    }
    return { extent: maxR, maxHeight: maxH };
  }, [buildings]);

  // Scene background + fog driven by time-of-day
  const { scene } = useThree();
  useEffect(() => {
    scene.background = new THREE.Color(sun.bgColor);
    scene.fog = new THREE.Fog(sun.fogColor, sun.fogNear, sun.fogFar);
  }, [sun, scene]);

  // Sun direction
  const sunPos: [number, number, number] = useMemo(() => [
    sun.sunDir.x * 1500,
    Math.max(40, sun.sunDir.y * 1500),
    sun.sunDir.z * 1500,
  ], [sun.sunDir]);

  return (
    <>
      <ambientLight intensity={sun.ambientIntensity} color={sun.ambientColor} />
      <directionalLight
        position={sunPos}
        intensity={sun.sunIntensity}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-left={-600}
        shadow-camera-right={600}
        shadow-camera-top={600}
        shadow-camera-bottom={-600}
        shadow-bias={-0.0005}
        color={sun.sunIntensity > 1.3 ? '#fff4d8' : '#ffc78a'}
      />
      <hemisphereLight
        args={[sun.bgColor, sun.groundColor, 0.45]}
      />

      <Sky
        distance={45000}
        sunPosition={[sun.sunDir.x, sun.sunDir.y, sun.sunDir.z]}
        inclination={sun.inclination}
        azimuth={sun.azimuth}
        rayleigh={sun.rayleigh}
        turbidity={10}
        mieCoefficient={0.005}
        mieDirectionalG={0.85}
      />

      {/* Procedural daytime clouds. Scattered well above the city
          so they don't visually interact with the camera at street
          level, but read clearly from any aerial angle. */}
      <Clouds material={THREE.MeshBasicMaterial}>
        <Cloud
          seed={1}
          segments={40}
          bounds={[260, 30, 260]}
          volume={70}
          color="#ffffff"
          opacity={0.65}
          fade={6000}
          position={[0, 600, -800]}
        />
        <Cloud
          seed={2}
          segments={40}
          bounds={[220, 25, 220]}
          volume={60}
          color="#f4f5f7"
          opacity={0.55}
          fade={6000}
          position={[1200, 660, 800]}
        />
        <Cloud
          seed={3}
          segments={36}
          bounds={[260, 28, 260]}
          volume={70}
          color="#ffffff"
          opacity={0.6}
          fade={6000}
          position={[-1500, 700, -1200]}
        />
        <Cloud
          seed={4}
          segments={32}
          bounds={[180, 20, 180]}
          volume={45}
          color="#eef0f4"
          opacity={0.45}
          fade={6000}
          position={[800, 580, -1600]}
        />
      </Clouds>

      {/* Distant hill silhouettes */}
      <MountainRidge color={sun.fogColor} />

      {/* Ground */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.05, 0]} receiveShadow>
        <planeGeometry args={[8000, 8000]} />
        <meshStandardMaterial color={sun.groundColor} roughness={1} metalness={0} />
      </mesh>

      {/* OSM land-use polygons (parks, parking, water, retail, etc.)
          paint colored districts directly onto the bare-earth ground. */}
      <LandUseLayer parcels={parcels} />

      {/* Per-building sidewalk halos — ring of light-grey pavement
          inflated 4m around every building footprint. */}
      <BuildingSidewalks buildings={buildings} />

      {/* Roads */}
      <Roads roads={roads} dayness={dayness} />

      {/* Yellow centre-line dashes on arterials */}
      <LaneMarkings roads={roads} />

      {/* Parked cars along curbs */}
      <ParkedCars roads={roads} />

      {/* Continuous slow traffic flow — one vehicle per major path */}
      <Traffic roads={roads} />

      {/* (Parker ground halo removed — replaced by the overhead pin marker.) */}

      {/* Buildings */}
      {buildings.map((b) => (
        <Building
          key={b.osm_id}
          b={b}
          isParker={b.osm_id === parkerId}
          isHovered={hovered === b.osm_id}
          isSelected={selected?.osm_id === b.osm_id}
          isCaptured={capturedSet.has(b.osm_id)}
          windowGlow={sun.windowGlow}
          onClick={(bb) => setSelected(bb)}
          onHover={setHovered}
        />
      ))}

      {/* Intel POIs */}
      <POIPins pois={pois} categories={categories} showLabels />

      {/* Procedural street life: trees, lamps, benches, signs, etc. */}
      <StreetProps roads={roads} />

      {/* Grass / green patches */}
      <GrassPatches roads={roads} extent={fit?.extent || 300} />

      {/* Overhead gold pin so Parker is findable at any angle */}
      {(() => {
        if (!parkerId) return null;
        const p = buildings.find(b => b.osm_id === parkerId);
        if (!p) return null;
        const [x, z] = project(p.lat, p.lng);
        return <ParkerMarker x={x} z={z} height={p.height_m} />;
      })()}

      {/* Google-Earth controls own the camera. The orchestrator only
          nudges the camera for initial fit, WASD glide, and one-shot
          fly-to on building select. */}
      <MapControls
        enablePan={true}
        panSpeed={1.2}
        rotateSpeed={0.7}
        zoomSpeed={1.1}
        minDistance={15}
        maxDistance={fit ? Math.max(2500, fit.extent * 6) : 2500}
        maxPolarAngle={Math.PI / 2.02}
        enableDamping
        dampingFactor={0.08}
        makeDefault
      />
      <CameraOrchestrator target={target} fit={fit} glide={glide} arrows={arrows} />
    </>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 *                          MAIN PAGE
 * ═════════════════════════════════════════════════════════════════ */
export default function Explorer({ token, onExitToMap, onExitToDashboard }: Props) {
  const [buildings, setBuildings] = useState<BuildingRow[]>([]);
  const [roads, setRoads] = useState<RoadRow[]>([]);
  const [parcels, setParcels] = useState<LandUseRow[]>([]);
  // anchorReady flips true once we've resolved Parker's coords from
  // the user's actual sheet data. Until then, downstream fetches wait
  // — they need the right (lat, lng) to query around.
  const [anchorReady, setAnchorReady] = useState(false);
  const [anchorMeta, setAnchorMeta] = useState<{ name: string; address: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [selected, setSelected] = useState<BuildingRow | null>(null);
  const [captured, setCaptured] = useState<Set<string>>(new Set());
  const [parkerId, setParkerId] = useState<string | null>(null);
  // 400m gives a substantial neighbourhood-scale view (~250–400 buildings)
  // without dwarfing buildings against the camera. Was 800 — too sparse,
  // and made the horizon-mountain backdrop visually dominate.
  const [radius] = useState(400);
  const [collectionOpen, setCollectionOpen] = useState(false);
  // Daytime is pinned; time-of-day controls were removed.
  const hour: TimeOfDay = 12;
  const [glide, setGlide] = useState({ dx: 0, dz: 0 });
  // Arrow-key rotation intent — yaw (horizontal) and pitch (vertical)
  // intent in [-1, 0, 1]. CameraOrchestrator turns this into a smooth
  // orbit around the current MapControls target each frame.
  const [arrows, setArrows] = useState({ yaw: 0, pitch: 0 });
  const [pois, setPois] = useState<IntelPOI[]>([]);
  const [poiCategories, setPoiCategories] = useState<Array<{ key: string; label: string; color: string }>>([]);

  // ─── Render mode: photoreal (Google 3D Tiles) vs stylized (procedural) ─
  const [googleKey, setGoogleKey] = useState<string>(() =>
    localStorage.getItem(GOOGLE_KEY_STORAGE) || '',
  );
  const [renderMode, setRenderMode] = useState<'photoreal' | 'stylized'>(() =>
    localStorage.getItem(GOOGLE_KEY_STORAGE) ? 'photoreal' : 'stylized',
  );
  const [keyModalOpen, setKeyModalOpen] = useState(false);
  const [keyDraft, setKeyDraft] = useState<string>('');

  const saveGoogleKey = (k: string) => {
    const cleaned = k.trim();
    if (cleaned) {
      localStorage.setItem(GOOGLE_KEY_STORAGE, cleaned);
      setGoogleKey(cleaned);
      setRenderMode('photoreal');
    } else {
      localStorage.removeItem(GOOGLE_KEY_STORAGE);
      setGoogleKey('');
      setRenderMode('stylized');
    }
    setKeyModalOpen(false);
  };

  const sun = useMemo(() => sunStateForHour(hour), [hour]);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  /* Resolve the real anchor (lat/lng for Parker) from the SAME bundle
     that supplies the POI data — guarantees POIs and anchor align by
     construction. The Intel bundle's property.lat/lng is exactly the
     coordinate the POIs were queried around. */
  useEffect(() => {
    let cancel = false;
    axios.get<IntelBundle>(`${API}/intel/property/parker`, {
      ...auth, timeout: 90000,
    }).then(r => {
      if (cancel) return;
      const prop = r.data?.property;
      if (prop && Number.isFinite(prop.lat) && Number.isFinite(prop.lng)) {
        PARKER_LAT = prop.lat;
        PARKER_LNG = prop.lng;
        setAnchorMeta({ name: prop.name, address: prop.address || '' });
      }
      // Stash the POI data we already have so the second Intel fetch
      // below short-circuits.
      if (r.data?.pois) setPois(r.data.pois);
      if (r.data?.categories) setPoiCategories(r.data.categories);
      setAnchorReady(true);
    }).catch(() => {
      if (cancel) return;
      // Fall back to the seeded defaults so the Explorer still shows
      // something rather than hanging on an unrecoverable error.
      setAnchorReady(true);
    });
    return () => { cancel = true; };
  }, [token]);

  /* Fetch buildings — gated on anchor resolution. */
  useEffect(() => {
    if (!anchorReady) return;
    let cancel = false;
    setLoading(true);
    setError(null);
    axios.get<BuildingsResponse>(`${API}/explore/buildings`, {
      ...auth,
      params: { lat: PARKER_LAT, lng: PARKER_LNG, radius },
      timeout: 90000,
    }).then(r => {
      if (cancel) return;
      setBuildings(r.data.buildings || []);
      // Find The Parker by proximity
      let best: { id: string; d: number } | null = null;
      for (const b of r.data.buildings || []) {
        const dx = (b.lat - PARKER_LAT) * 111320;
        const dy = (b.lng - PARKER_LNG) * 111320 * Math.cos(PARKER_LAT * Math.PI / 180);
        const d = Math.hypot(dx, dy);
        if (d < 80 && (!best || d < best.d)) best = { id: b.osm_id, d };
      }
      setParkerId(best?.id || null);
    }).catch(err => {
      if (cancel) return;
      const detail = err?.response?.data?.detail || err?.message || 'unknown';
      setError(`Could not load buildings: ${detail}`);
    }).finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [anchorReady, radius, token]);

  /* Fetch roads — gated on anchor. */
  useEffect(() => {
    if (!anchorReady) return;
    let cancel = false;
    axios.get<RoadsResponse>(`${API}/explore/roads`, {
      ...auth,
      params: { lat: PARKER_LAT, lng: PARKER_LNG, radius },
      timeout: 90000,
    }).then(r => {
      if (cancel) return;
      setRoads(r.data.roads || []);
    }).catch(() => { /* tolerate */ });
    return () => { cancel = true; };
  }, [anchorReady, radius, token]);

  /* Fetch land-use parcels — gated on anchor. */
  useEffect(() => {
    if (!anchorReady) return;
    let cancel = false;
    axios.get<LandUseResponse>(`${API}/explore/landuse`, {
      ...auth,
      params: { lat: PARKER_LAT, lng: PARKER_LNG, radius },
      timeout: 90000,
    }).then(r => {
      if (cancel) return;
      setParcels(r.data.parcels || []);
    }).catch(() => { /* tolerate */ });
    return () => { cancel = true; };
  }, [anchorReady, radius, token]);

  /* Fetch captures */
  useEffect(() => {
    let cancel = false;
    axios.get<{ buildings: { osm_id: string }[] }>(`${API}/explore/collection`, {
      ...auth, timeout: 30000,
    }).then(r => {
      if (cancel) return;
      setCaptured(new Set((r.data.buildings || []).map(b => b.osm_id)));
    }).catch(() => { /* tolerate */ });
    return () => { cancel = true; };
  }, [token]);

  /* Keyboard:  WASD = pan, Arrows = orbit (yaw + pitch), Shift = boost,
                F = refit. */
  useEffect(() => {
    const keys: Record<string, boolean> = {};
    const onDown = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement;
      if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA')) return;
      const k = e.key.toLowerCase();
      keys[k] = true;
      if (k === 'f') {
        setSelected(null);
        setGlide({ dx: 0, dz: 0 });
      }
      // Stop arrow-key page scroll when we're hijacking them for rotation
      if (k.startsWith('arrow')) e.preventDefault();
    };
    const onUp = (e: KeyboardEvent) => { keys[e.key.toLowerCase()] = false; };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);

    let raf = 0;
    const tick = () => {
      const speed = keys['shift'] ? 5.4 : 2.4;
      // Translation: WASD only. Arrow keys are dedicated to rotation now.
      let dx = 0, dz = 0;
      if (keys['w']) dz -= speed;
      if (keys['s']) dz += speed;
      if (keys['a']) dx -= speed;
      if (keys['d']) dx += speed;
      setGlide(g => (g.dx === dx && g.dz === dz) ? g : { dx, dz });

      // Rotation: arrow keys → unit-vector intent; orchestrator scales
      // by per-frame angular speed.
      let yaw = 0, pitch = 0;
      if (keys['arrowleft'])  yaw   -= 1;
      if (keys['arrowright']) yaw   += 1;
      if (keys['arrowup'])    pitch -= 1;
      if (keys['arrowdown'])  pitch += 1;
      setArrows(a => (a.yaw === yaw && a.pitch === pitch) ? a : { yaw, pitch });

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      cancelAnimationFrame(raf);
    };
  }, []);

  const captureSelected = async () => {
    if (!selected) return;
    try {
      await axios.post(`${API}/explore/collection`, {
        osm_id:       selected.osm_id,
        name:         selected.name || (selected.osm_id === parkerId ? 'The Parker Apartments' : 'Unnamed building'),
        address:      selected.address,
        lat:          selected.lat,
        lng:          selected.lng,
        height_m:     selected.height_m,
        footprint_m2: selected.footprint_m2,
        tier:         selected.tier,
        tier_color:   selected.tier_color,
      }, auth);
      setCaptured(prev => new Set(prev).add(selected.osm_id));
    } catch { /* ignore */ }
  };
  const releaseSelected = async () => {
    if (!selected) return;
    try {
      await axios.delete(`${API}/explore/collection/${encodeURIComponent(selected.osm_id)}`, auth);
      setCaptured(prev => { const n = new Set(prev); n.delete(selected.osm_id); return n; });
    } catch { /* ignore */ }
  };

  const displayName = (b: BuildingRow): string => {
    if (b.osm_id === parkerId) return 'The Parker Apartments';
    if (b.name) return b.name;
    if (b.address) return b.address;
    return 'Unnamed building';
  };
  const selectedIsCaptured = selected ? captured.has(selected.osm_id) : false;

  // (Time-of-day labels removed.)

  return (
    // Outer error boundary catches any render-time throw from the
    // Explorer tree — including the surrounding UI overlays, not just
    // the Canvas. Without this, an exception in the top bar, the
    // photoreal key modal, the legend, etc. would unmount the whole
    // page and leave the browser showing pure white, which is exactly
    // the symptom of the cold-load bug.
    <CanvasErrorBoundary fallback={
      <div style={{
        position: 'fixed', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 16,
        background: '#050E1A',
        color: '#F0E8D8', fontFamily: 'EB Garamond, serif', textAlign: 'center',
        padding: 32,
      }}>
        <div style={{ fontSize: 22, color: '#C9A96E', letterSpacing: '0.04em' }}>
          Explorer failed to load
        </div>
        <div style={{ fontSize: 14, color: '#7A8CA0', maxWidth: 480 }}>
          Something went wrong rendering the 3D page. Refreshing should
          recover it. Check the browser console for the underlying error.
        </div>
        <button onClick={onExitToDashboard} style={{
          marginTop: 8, padding: '10px 22px',
          background: '#C9A96E', color: '#0a1322', border: 'none',
          fontSize: 14, fontFamily: 'EB Garamond, serif',
          letterSpacing: '0.04em', cursor: 'pointer', borderRadius: 2,
        }}>
          Back to Dashboard
        </button>
      </div>
    }>
    <div style={{
      position: 'fixed', inset: 0,
      background: '#0a1322',
      color: '#F0F0F0',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter", system-ui, sans-serif',
      fontWeight: 300,
      overflow: 'hidden',
    }}>
      <style>{`
        @keyframes coastSpin { to { transform: rotate(360deg); } }
        @keyframes coastFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes coastFadeInRight {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        /* Time slider — premium gold thumb */
        input.coast-tod {
          -webkit-appearance: none; appearance: none;
          width: 100%; height: 4px;
          background: rgba(255,255,255,0.14);
          border-radius: 2px; outline: none;
        }
        input.coast-tod::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 14px; height: 14px; border-radius: 7px;
          background: #C9A96E;
          border: 2px solid #0a1322;
          cursor: grab; box-shadow: 0 0 0 1px #C9A96E66;
        }
        input.coast-tod::-moz-range-thumb {
          width: 14px; height: 14px; border-radius: 7px;
          background: #C9A96E; border: 2px solid #0a1322; cursor: grab;
        }
      `}</style>

      {/* 3D Canvas — wrapped in a class-based Error Boundary so any
          runtime exception in the Three.js tree (a plugin throw, a
          missing asset, a malformed building ring) surfaces a
          recoverable fallback instead of blanking the entire page. */}
      <CanvasErrorBoundary fallback={
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 16,
          background: '#050E1A',
          color: '#F0E8D8', fontFamily: 'EB Garamond, serif', textAlign: 'center',
          padding: 32,
        }}>
          <div style={{ fontSize: 22, color: '#C9A96E', letterSpacing: '0.04em' }}>
            3D scene failed to load
          </div>
          <div style={{ fontSize: 14, color: '#7A8CA0', maxWidth: 480 }}>
            Something went wrong inside the Explorer canvas. Refreshing the
            page should recover it. Check the browser console for details.
          </div>
        </div>
      }>
      <Canvas
        shadows
        camera={{ position: [280, 200, 280], fov: 50, near: 1, far: 12000 }}
        gl={{
          antialias: true,
          powerPreference: 'high-performance',
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.25,
        }}
        dpr={[1, 2]}
        style={{ position: 'absolute', inset: 0 }}>
        <Suspense fallback={null}>
          {renderMode === 'photoreal' && googleKey && anchorReady ? (
            <>
              <PhotorealCity
                apiKey={googleKey}
                anchorLat={PARKER_LAT}
                anchorLng={PARKER_LNG}
              />
              {/* Same Intel POIs from /intel/property/parker — they sit
                  in the same local frame because PhotorealCity reorients
                  the photo tiles so PARKER lands at world origin. */}
              <POIPins pois={pois} categories={poiCategories} showLabels />
              {/* minDistance bumped to 80 m so the user can't zoom in past
                  Google's max LOD — beyond that point you start seeing
                  individual blob faces on trees and stretched textures. */}
              <MapControls
                enablePan
                panSpeed={1.4}
                rotateSpeed={0.65}
                zoomSpeed={1.2}
                minDistance={80}
                maxDistance={5000}
                maxPolarAngle={Math.PI / 2.02}
                enableDamping
                dampingFactor={0.08}
                makeDefault
              />
              {/* WASD glide + arrow-key 360° orbit also work in Photoreal */}
              <CameraOrchestrator
                target={null} fit={null}
                glide={glide} arrows={arrows}
              />
            </>
          ) : (
            <Scene
              buildings={buildings}
              roads={roads}
              parcels={parcels}
              hovered={hovered}
              setHovered={setHovered}
              selected={selected}
              setSelected={setSelected}
              capturedSet={captured}
              parkerId={parkerId}
              sun={sun}
              glide={glide}
              arrows={arrows}
              dayness={1.0}
              pois={pois}
              categories={poiCategories}
            />
          )}
          {/* Cinematic post-processing pass — SSAO grounds buildings to
              the pavement, Bloom kisses the brightest highlights, the
              corner vignette focuses the eye on the city centre. Both
              modes share the same FX so the look stays cohesive. */}
          {/* multisampling 4 = MSAA edges on geometry (jagged building
              silhouettes get smooth). SSAO samples bumped for cleaner
              corner shadow; bloom intensity up + threshold down so
              sunlit walls + window highlights actually glow. */}
          <EffectComposer multisampling={4} enableNormalPass>
            <SSAO
              blendFunction={BlendFunction.MULTIPLY}
              samples={32}
              rings={5}
              distanceThreshold={0.55}
              distanceFalloff={0.12}
              rangeThreshold={0.012}
              rangeFalloff={0.004}
              luminanceInfluence={0.75}
              radius={22}
              bias={0.025}
              intensity={1.8}
              color={new THREE.Color('#0a0a14')}
            />
            <Bloom
              intensity={0.6}
              luminanceThreshold={0.72}
              luminanceSmoothing={0.35}
              mipmapBlur
            />
            {/* Tiny saturation + contrast lift — Google's photoreal tiles
                are slightly washed out by default; bumping these brings
                the colour back to "real Apple-Maps-vibrant". */}
            <HueSaturation hue={0} saturation={0.18} />
            <BrightnessContrast brightness={0.02} contrast={0.10} />
            <Vignette
              eskil={false}
              offset={0.18}
              darkness={0.52}
            />
            {/* SMAA cleans up the jagged polygon edges on the
                photogrammetric mesh (tree blobs especially) where
                MSAA alone leaves stair-stepping. */}
            <SMAA />
          </EffectComposer>
        </Suspense>
      </Canvas>
      </CanvasErrorBoundary>

      {/* ─── Top bar ─── */}
      <div style={{
        position: 'absolute', top: 18, left: 18, right: 18,
        display: 'flex', alignItems: 'center', gap: 10,
        pointerEvents: 'none',
      }}>
        <div style={glassPill()}>
          <span style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 18, fontWeight: 800, color: ACCENT,
            letterSpacing: '0.02em',
          }}>COAST</span>
          <span style={{
            color: '#8FA0B8', fontSize: 10, marginLeft: 8,
            letterSpacing: '0.16em', textTransform: 'uppercase',
            fontWeight: 600,
          }}>Explorer · LA</span>
        </div>
        <div style={{ ...glassPill(), flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
          <span style={{
            color: ACCENT, fontSize: 9, fontWeight: 700,
            letterSpacing: '0.20em', textTransform: 'uppercase',
          }}>Anchor</span>
          <span style={{ color: '#F0F0F0', fontSize: 12, fontWeight: 500 }}>
            {anchorMeta?.name || 'Resolving…'}
          </span>
          {anchorMeta?.address && (
            <span style={{ color: '#8FA0B8', fontSize: 9, marginTop: 1 }}>
              {anchorMeta.address}
            </span>
          )}
        </div>

        <span style={{ flex: 1 }} />

        {/* Photoreal / Stylized toggle */}
        <div style={{
          ...glassPanel(), padding: 3, display: 'flex', gap: 2,
        }}>
          <button
            onClick={() => {
              if (!googleKey) {
                setKeyDraft('');
                setKeyModalOpen(true);
              } else {
                setRenderMode('photoreal');
              }
            }}
            style={{
              background: renderMode === 'photoreal' ? ACCENT : 'transparent',
              color: renderMode === 'photoreal' ? '#0a1322' : '#F0F0F0',
              border: 'none', cursor: 'pointer',
              padding: '6px 12px', borderRadius: 12,
              fontFamily: 'inherit', fontSize: 10,
              fontWeight: 700, letterSpacing: '0.12em',
              textTransform: 'uppercase',
            }}>
            Photoreal
          </button>
          <button
            onClick={() => setRenderMode('stylized')}
            style={{
              background: renderMode === 'stylized' ? ACCENT : 'transparent',
              color: renderMode === 'stylized' ? '#0a1322' : '#F0F0F0',
              border: 'none', cursor: 'pointer',
              padding: '6px 12px', borderRadius: 12,
              fontFamily: 'inherit', fontSize: 10,
              fontWeight: 700, letterSpacing: '0.12em',
              textTransform: 'uppercase',
            }}>
            Stylized
          </button>
        </div>

        <button
          onClick={() => setCollectionOpen(true)}
          style={{
            ...glassBtn(),
            background: `${ACCENT}22`, border: `1px solid ${ACCENT}`,
            color: ACCENT, fontWeight: 700,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            fontSize: 10,
          }}>
          Portfolio ({captured.size})
        </button>
        <button onClick={onExitToMap} style={glassBtn()}>← Map</button>
        <button onClick={onExitToDashboard} style={glassBtn()}>Dashboard →</button>
      </div>

      {/* (Time of day panel removed — Explorer is locked to daytime.) */}

      {/* ─── POI category legend ───
          Renders the same ten lifestyle categories the Intel panel
          surfaces, with venue counts. Tells the user what every
          colored pin in the scene means. */}
      {poiCategories.length > 0 && (
        <div style={{
          position: 'absolute', top: 70, right: 18, zIndex: 4,
          ...glassPanel(),
          padding: '12px 14px',
          width: 200,
        }}>
          <div style={{
            color: ACCENT, fontSize: 9, fontWeight: 700,
            letterSpacing: '0.20em', textTransform: 'uppercase',
            marginBottom: 8,
          }}>
            Lifestyle pins
          </div>
          {poiCategories.map(c => {
            const count = pois.filter(p => p.category === c.key).length;
            if (count === 0) return null;
            return (
              <div key={c.key} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '3px 0', fontSize: 11,
              }}>
                <span style={{
                  width: 10, height: 10, borderRadius: 5,
                  background: c.color, flex: '0 0 auto',
                  boxShadow: `0 0 6px ${c.color}88`,
                }} />
                <span style={{ flex: 1, color: '#F0F0F0', fontWeight: 500 }}>
                  {c.label}
                </span>
                <span style={{
                  color: '#8FA0B8', fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums', fontSize: 10,
                }}>
                  {count}
                </span>
              </div>
            );
          })}
          <div style={{
            marginTop: 8, paddingTop: 8,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            color: '#8FA0B8', fontSize: 10, lineHeight: 1.4,
          }}>
            Same venues the Intel panel pulls — every cafe, gym, transit
            stop, and grocery within The Parker's 15-min ring.
          </div>
        </div>
      )}

      {/* ─── Loading state ─── */}
      {loading && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center', pointerEvents: 'none',
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 19,
            border: `2px solid ${ACCENT}55`,
            borderTopColor: ACCENT,
            margin: '0 auto 14px',
            animation: 'coastSpin 0.9s linear infinite',
          }} />
          <div style={{ fontSize: 12, color: '#9BA8BD', letterSpacing: '0.04em' }}>
            Building the city… pulling {radius}m of OSM footprints + heights.
          </div>
        </div>
      )}
      {error && !loading && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          ...glassPanel(), padding: 20, maxWidth: 360,
          color: '#CC2200', fontSize: 12,
        }}>
          {error}
        </div>
      )}

      {/* ─── Hover label ─── */}
      {hovered && !selected && (() => {
        const b = buildings.find(x => x.osm_id === hovered);
        if (!b) return null;
        return (
          <div style={{
            position: 'absolute', left: '50%', bottom: 64,
            transform: 'translateX(-50%)',
            ...glassPanel(),
            padding: '8px 16px',
            display: 'flex', gap: 12, alignItems: 'center',
            animation: 'coastFadeIn 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
            fontSize: 12, pointerEvents: 'none',
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: 4,
              background: b.tier_color, boxShadow: `0 0 8px ${b.tier_color}88`,
            }} />
            <span style={{ color: '#F0F0F0', fontWeight: 500 }}>
              {displayName(b)}
            </span>
            <span style={{ color: '#8FA0B8', fontSize: 10 }}>
              {b.tier} · {Math.round(b.height_m)}m
            </span>
          </div>
        );
      })()}

      {/* ─── Selected building card ─── */}
      {selected && (
        <div style={{
          position: 'absolute', top: 70, left: 18,
          width: 320, ...glassPanel(),
          padding: '18px 20px',
          animation: 'coastFadeIn 0.32s cubic-bezier(0.16, 1, 0.3, 1)',
        }}>
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6,
          }}>
            <span style={{
              padding: '2px 8px', borderRadius: 100,
              background: selected.tier_color, color: '#FFFFFF',
              fontSize: 9, fontWeight: 700, letterSpacing: '0.16em',
              textTransform: 'uppercase',
            }}>
              {selected.tier}
            </span>
            <span style={{ flex: 1 }} />
            <button
              onClick={() => setSelected(null)}
              style={{
                background: 'transparent', border: 'none',
                color: '#8FA0B8', fontSize: 14, cursor: 'pointer',
                padding: 0, lineHeight: 1,
              }}>×</button>
          </div>
          <div style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 22, fontWeight: 800, color: '#F0F0F0',
            lineHeight: 1.15, marginBottom: 2,
          }}>
            {displayName(selected)}
          </div>
          {selected.address && (
            <div style={{
              color: '#8FA0B8', fontSize: 11, marginBottom: 12,
            }}>{selected.address}</div>
          )}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
            margin: '12px 0 16px',
          }}>
            <Stat label="Height"      value={`${Math.round(selected.height_m)} m`} />
            <Stat label="Footprint"   value={`${Math.round(selected.footprint_m2)} m²`} />
            <Stat label="Levels"      value={selected.levels ? String(selected.levels) : String(Math.max(1, Math.round(selected.height_m / 3.2)))} />
            <Stat label="OSM tag"     value={selected.use} />
          </div>
          {selectedIsCaptured ? (
            <button onClick={releaseSelected} style={{
              ...glassBtn(),
              width: '100%', padding: '10px 14px',
              background: 'transparent',
              border: `1px solid ${selected.tier_color}66`,
              color: selected.tier_color, fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase',
              fontSize: 10,
            }}>
              ✓ Captured · release
            </button>
          ) : (
            <button onClick={captureSelected} style={{
              ...glassBtn(),
              width: '100%', padding: '10px 14px',
              background: selected.tier_color, color: '#FFFFFF',
              border: `1px solid ${selected.tier_color}`, fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase',
              fontSize: 10,
              boxShadow: `0 0 22px ${selected.tier_color}44`,
            }}>
              Capture building
            </button>
          )}
        </div>
      )}

      {/* ─── Hint ─── */}
      {!loading && !selected && buildings.length > 0 && (
        <div style={{
          position: 'absolute', bottom: 24, left: 18,
          ...glassPill(),
          padding: '8px 14px', fontSize: 11, color: '#9BA8BD',
        }}>
          Drag to pan · right-drag to rotate · scroll to zoom · WASD to move · ← → ↑ ↓ to rotate · F to refit
        </div>
      )}

      {/* ─── Stats footer ─── */}
      {!loading && buildings.length > 0 && (
        <div style={{
          position: 'absolute', bottom: 24, right: 18,
          ...glassPill(),
          padding: '8px 14px', fontSize: 11, color: '#9BA8BD',
          letterSpacing: '0.04em',
        }}>
          <span style={{ color: ACCENT, fontWeight: 700, letterSpacing: '0.1em' }}>
            {buildings.length}
          </span>
          <span style={{ marginLeft: 6 }}>buildings · {radius}m radius</span>
        </div>
      )}

      {/* ─── Portfolio overlay ─── */}
      {collectionOpen && (
        <CollectionOverlay
          token={token}
          onClose={() => setCollectionOpen(false)}
        />
      )}

      {/* ─── Google API key setup modal ─── */}
      {keyModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(7, 11, 20, 0.88)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 40,
          animation: 'coastFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        }}>
          <div style={{
            ...glassPanel(), padding: 28,
            maxWidth: 560, width: '100%',
            color: '#F0F0F0', lineHeight: 1.5,
          }}>
            <div style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 22, fontWeight: 800, color: ACCENT,
              marginBottom: 6,
            }}>
              Photoreal LA — setup
            </div>
            <div style={{ color: '#9BA8BD', fontSize: 12, marginBottom: 18 }}>
              Paste a Google Maps Tiles API key below to stream Google's
              real photogrammetric 3D mesh of Los Angeles (the same data
              Google Earth uses). Free up to ~28K tile requests per month
              under Google's $200/mo Map Platform credit.
            </div>

            <div style={{
              padding: 14, marginBottom: 16,
              background: 'rgba(201,169,110,0.08)',
              border: `1px solid ${ACCENT}44`,
              borderRadius: 10, fontSize: 11, color: '#cbd2dc',
            }}>
              <div style={{
                fontSize: 9, fontWeight: 700, color: ACCENT,
                letterSpacing: '0.18em', textTransform: 'uppercase',
                marginBottom: 8,
              }}>
                How to get a key (≈3 minutes)
              </div>
              <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>Open <span style={{ color: ACCENT }}>console.cloud.google.com</span> and sign in.</li>
                <li>Create a new project (or pick an existing one).</li>
                <li>Enable billing on the project (credit card required for ID
                    — Google's $200/month Map Platform credit makes dev use free).</li>
                <li>APIs &amp; Services → Library → search "Map Tiles API" → Enable.</li>
                <li>APIs &amp; Services → Credentials → Create Credentials → API key.</li>
                <li>Edit the key, restrict it to "Map Tiles API" and HTTP referrer:
                    <code style={{
                      display: 'block', padding: '4px 8px', marginTop: 4,
                      background: 'rgba(0,0,0,0.3)', borderRadius: 4,
                      fontSize: 10, color: '#e8c34a',
                    }}>https://coast-terminal.pages.dev/*</code></li>
                <li>Copy the key and paste it below.</li>
              </ol>
            </div>

            <input
              type="text"
              value={keyDraft}
              onChange={(e) => setKeyDraft(e.target.value)}
              placeholder="AIzaSy..."
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'rgba(0,0,0,0.35)',
                border: `1px solid ${ACCENT}44`,
                borderRadius: 10,
                color: '#F0F0F0',
                fontSize: 12,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                marginBottom: 14,
                outline: 'none',
              }}
            />

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setKeyModalOpen(false)}
                style={{
                  ...glassBtn(), padding: '8px 16px', fontSize: 11,
                }}>
                Cancel
              </button>
              {googleKey && (
                <button
                  onClick={() => saveGoogleKey('')}
                  style={{
                    ...glassBtn(), padding: '8px 16px', fontSize: 11,
                    border: '1px solid #CC2200', color: '#CC2200',
                  }}>
                  Clear saved key
                </button>
              )}
              <button
                onClick={() => saveGoogleKey(keyDraft)}
                disabled={!keyDraft.trim()}
                style={{
                  ...glassBtn(),
                  padding: '8px 16px', fontSize: 11,
                  background: keyDraft.trim() ? ACCENT : `${ACCENT}55`,
                  color: keyDraft.trim() ? '#0a1322' : '#9BA8BD',
                  fontWeight: 700, letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  cursor: keyDraft.trim() ? 'pointer' : 'not-allowed',
                }}>
                Save &amp; enable
              </button>
            </div>

            <div style={{
              marginTop: 14, color: '#7a8294', fontSize: 10,
            }}>
              Stored in this browser only (localStorage). Restrict the key to
              your domain in the Cloud Console so it's safe to ship in the
              frontend.
            </div>
          </div>
        </div>
      )}
    </div>
    </CanvasErrorBoundary>
  );
}


/* ── Portfolio overlay ───────────────────────────────────────────────── */
interface FullCollectionRow {
  osm_id: string; name: string; address: string;
  lat: number; lng: number; height_m: number; footprint_m2: number;
  tier: string; tier_color: string;
  discovered_at: string | null;
}

function CollectionOverlay({
  token, onClose,
}: { token: string; onClose: () => void }) {
  const [rows, setRows] = useState<FullCollectionRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get<{ buildings: FullCollectionRow[] }>(
      `${API}/explore/collection`,
      { headers: { Authorization: `Bearer ${token}` } },
    ).then(r => setRows(r.data.buildings || []))
     .catch(() => setRows([]))
     .finally(() => setLoading(false));
  }, [token]);

  const tierOrder = ['Trophy Asset', 'Landmark', 'Epic', 'Rare', 'Common'];
  const byTier: Record<string, FullCollectionRow[]> = {};
  for (const r of rows) (byTier[r.tier] = byTier[r.tier] || []).push(r);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(10, 19, 34, 0.88)',
      backdropFilter: 'blur(18px)',
      WebkitBackdropFilter: 'blur(18px)',
      animation: 'coastFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
      overflowY: 'auto',
      padding: '60px 40px',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 30,
        }}>
          <div style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 32, fontWeight: 800, color: ACCENT,
          }}>Property Portfolio</div>
          <div style={{ color: '#8FA0B8', fontSize: 12 }}>
            {rows.length} buildings collected
          </div>
          <span style={{ flex: 1 }} />
          <button onClick={onClose} style={{
            ...glassBtn(), padding: '10px 18px', fontSize: 11,
          }}>Close ×</button>
        </div>

        {loading && <div style={{ color: '#9BA8BD', fontSize: 13 }}>Loading…</div>}

        {!loading && rows.length === 0 && (
          <div style={{
            color: '#9BA8BD', fontSize: 13, lineHeight: 1.6,
            padding: 40, textAlign: 'center',
            ...glassPanel(), maxWidth: 480, margin: '40px auto',
          }}>
            <div style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 18, color: '#F0F0F0', marginBottom: 8,
            }}>Portfolio is empty</div>
            Click any building in the Explorer, then tap "Capture" to add it to your tracked set.
          </div>
        )}

        {!loading && rows.length > 0 && tierOrder.map(tier => {
          const rs = byTier[tier];
          if (!rs || rs.length === 0) return null;
          return (
            <div key={tier} style={{ marginBottom: 32 }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10,
              }}>
                <div style={{
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontSize: 18, fontWeight: 700, color: '#F0F0F0',
                  letterSpacing: '0.02em',
                }}>{tier}</div>
                <div style={{ color: '#8FA0B8', fontSize: 11 }}>{rs.length} captured</div>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 14,
              }}>
                {rs.map(r => (
                  <div key={r.osm_id} style={{
                    ...glassPanel(),
                    padding: 14,
                    borderLeft: `3px solid ${r.tier_color}`,
                    transition: 'transform 0.18s, box-shadow 0.18s',
                    cursor: 'default',
                  }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                      (e.currentTarget as HTMLDivElement).style.boxShadow = `0 8px 24px rgba(0,0,0,0.5), 0 0 22px ${r.tier_color}44`;
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                      (e.currentTarget as HTMLDivElement).style.boxShadow = '';
                    }}>
                    <div style={{
                      display: 'inline-block',
                      padding: '2px 7px', borderRadius: 100,
                      background: r.tier_color, color: '#FFFFFF',
                      fontSize: 8, fontWeight: 700,
                      letterSpacing: '0.16em', textTransform: 'uppercase',
                      marginBottom: 6,
                    }}>{r.tier}</div>
                    <div style={{
                      fontFamily: 'Fraunces, Georgia, serif',
                      fontSize: 14, fontWeight: 700,
                      color: '#F0F0F0', lineHeight: 1.2, marginBottom: 2,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>{r.name || 'Unnamed'}</div>
                    {r.address && (
                      <div style={{
                        color: '#8FA0B8', fontSize: 10,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap', marginBottom: 6,
                      }}>{r.address}</div>
                    )}
                    <div style={{
                      display: 'flex', gap: 10, fontSize: 9,
                      color: '#8FA0B8', letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}>
                      <span>{Math.round(r.height_m)}m</span>
                      <span>{Math.round(r.footprint_m2).toLocaleString()} m²</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Tiny shared style helpers ─────────────────────────────────────── */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{
        color: '#8FA0B8', fontSize: 9, fontWeight: 700,
        letterSpacing: '0.14em', textTransform: 'uppercase',
        marginBottom: 2,
      }}>{label}</div>
      <div style={{
        fontFamily: 'Fraunces, Georgia, serif',
        color: '#F0F0F0', fontSize: 15, fontWeight: 700,
      }}>{value}</div>
    </div>
  );
}

function glassPanel(): React.CSSProperties {
  return {
    background: 'rgba(14, 22, 38, 0.78)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    borderRadius: 16,
    boxShadow: '0 12px 36px rgba(0, 0, 0, 0.45)',
    pointerEvents: 'auto',
  };
}
function glassPill(): React.CSSProperties {
  return {
    ...glassPanel(),
    padding: '8px 14px',
    display: 'inline-flex', alignItems: 'center',
  };
}
function glassBtn(): React.CSSProperties {
  return {
    ...glassPill(),
    padding: '8px 14px',
    cursor: 'pointer',
    color: '#F0F0F0', fontFamily: 'inherit',
    fontSize: 11, letterSpacing: '0.04em',
  };
}
